import React, { useEffect, useMemo, useState, useRef } from "react";
import { reportCellState } from "../../utils/reportsFolderFiles";
import { createPortal } from "react-dom";
import { useNavigate, useHref } from "react-router-dom";
import { normalizeRole, canAccessPage, getColumnLabels } from "../../config/roles";
import {
  Box,
  Checkbox,
  Badge,
  Button,
  Input,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  HStack,
  Center,
  Spinner,
  Select,
  Text,
  VStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  FormHelperText,
  useDisclosure,
  Portal,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFolder,
  faFile,
  faArrowUp,
  faArrowDown,
  faShare,
  faFileAudio,
} from "@fortawesome/free-solid-svg-icons";
import {
  bucketName_files,
  tableName,
  data_column,
  file_reference_column,
  royal_blue,
  turquoise,
  colid,
  linkColid,
  linkURL,
  formCondition,
  defaultItemsPerPage,
  filled_button_form,
  empty_button_form,
  submissions_arrayUrls,
  singleLinkSubmissionUrl,
} from "../env.js";
import ReactModal from "react-modal";
import DraggableResizableModal from "../DraggableResizableModal";
import AudioPlayerModal from "./audio/AudioPlayerModal";
import AudioRecorderModal from "./audio/AudioRecorderModal.js";
import ConfirmStatusCell, { ConfirmStatusHeaderCell } from "./ConfirmStatusCell";
import { isRowDimmed, dimmedRowSx, selectCheckboxSx } from "./rowDimming";
import { convertDate, timeToMinutes } from "../../utils/utils.js";
import LateBy from "../LateBy.js";
import { Link } from "react-router-dom";
import { getSupabase, supabasePublic, supabaseSchema } from "../supabaseClient";
import {
  cacheSchemaCandidates,
  clientForSchema,
  groupPathsBySchema,
} from "../../utils/reportsPathSchema";
import { getSignedUrl } from "../../utils/signedUrls";
import { dbForRow } from "../../utils/rowSchema";
import { rowPhonesNormalized } from "../../utils/smsPhone";
import { buildReportsFolder } from "../../utils/reportsPath";
import { filterTranscriptsForVisitDate } from "../../utils/audioMetadata";
import { deleteFilesWithCacheCleanup } from "../../utils/storageUtils";
import { FORM_CONFIG } from "../env";
import { useToast } from "@chakra-ui/react";
import SearchComponent from "../SearchComponent";
import {
  getLiveTranscriptionSetting,
} from "../../utils/utils";
import {
  getPatientPageVisitPathSegment,
  buildVisitNavigation,
} from "../../utils/visitPathUtils";

import PasswordModal from "../PasswordModal";
import {
  extractAllSubmissionLinks,
  submissionLinksFromRow,
} from "../../utils/submissionUtils.js"; // <-- ADD THIS LINE
import {
  getMetadataColor,
  getMetadataBgColor,
  isHighlightedCategory,
  METADATA_COLORS,
  ORDERS_COLOR,
  getOrdersBgColor,
} from "../../utils/metadataColors.js";
import { useReportFilters } from "../../hooks/useReportFilters";
import {
  scheduleFolders,
  schemasForFolders,
  matchOrdersToSchedules,
} from "../../utils/scheduleOrders";
import ReportsFilterBar from "../reports/ReportsFilterBar";
import {
  classifyReport,
  REPORT_TYPE_SHORT_LABELS,
} from "../../utils/reportProcessingUtils";
import { dbgLog } from "../../utils/appDebugFlags";
import { convertScheduledTime } from "../../utils/scheduleTimeDisplay.js";
import { hitsRowControl, isMiddleButton, wantsNewTab, newTabHref } from "../../utils/rowClickIntent";
import {
  continueOcrExtractionInBatches,
  continueOcrIncompleteToast,
  continueOcrProgressLabel,
  continueOcrUpdatedTruncation,
} from "../../utils/continueOcrExtraction";
import PhoneStatusBadge from "../sms/PhoneStatusBadge";

/** Where a clicked row opens, for this viewer. `null` when the row has no id. */
const visitNavFor = (row, userRole) =>
  buildVisitNavigation(
    row,
    canAccessPage(userRole, "clinicPage") ? "clinic" : "visit",
  );

const rLog = (...args) => dbgLog("results-table", ...args);

// "Is this folder entry a file (rather than a subfolder)?"
//
// This exact regex was previously copy-pasted at 7 sites: the three
// file_classification_cache hydration filters, the Select All / Deselect All
// controls, and the row renderer's `isFile`. They MUST agree — if the row
// renderer treats .heic as a file but the hydration filter does not, the file
// lists but never gets a classification, so it shows as Unclassified forever and
// can never carry an Order badge. One constant makes that drift impossible.
//
// heic/heif/tiff/tif are included because that is what an iPad camera capture
// delivers, and the backend classifier accepts them (ai_assistant.IMAGE_EXTENSIONS).
// Safe to share a single RegExp instance: no /g or /y flag, so `.test()` keeps no
// lastIndex state between calls.
const REPORT_FILE_RE =
  /\.(pdf|csv|txt|png|jpg|jpeg|gif|bmp|webp|svg|heic|heif|tiff|tif)$/i;

// Schedule row gradients — ordered so adjacent entries are far apart on
// the color wheel (green ↔ magenta ↔ orange ↔ cyan …) and assignment is
// POSITIONAL (by row index, not patient hash). That guarantees:
//   - no two rows in the same page share a color until index ≥ palette length
//   - consecutive rows never look like the same hue
//
// Hue-interleaved pairs: first hex is applied to patient name + scheduled time
// only (cells already wired to _avC1). Second hex is unused for now.
// Completed visits stay muted slate (ROW_COMPLETED_GRADIENT_HEX).
const ROW_GRADIENTS_HEX = [
  ["#047857", "#065f46"], // emerald
  ["#86198f", "#701a75"], // fuchsia
  ["#ea580c", "#c2410c"], // orange
  ["#0891b2", "#155e75"], // cyan
  ["#92400e", "#78350f"], // amber
  ["#991b1b", "#991b1b"], // red
  ["#0d9488", "#115e59"], // teal
  ["#6d28d9", "#5b21b6"], // violet
];
const ROW_COMPLETED_GRADIENT_HEX = ['#9ca3af', '#6b7280']; // gray-400 → gray-500 (stronger de-emphasis)

function pickRowGradientHex(ind, completed) {
  if (completed) return ROW_COMPLETED_GRADIENT_HEX;
  return ROW_GRADIENTS_HEX[((ind ?? 0) % ROW_GRADIENTS_HEX.length + ROW_GRADIENTS_HEX.length) % ROW_GRADIENTS_HEX.length];
}

// "Completed" iff the appointment time has passed AND at least one
// recording exists. Mirrors pages/medstudent/utils.js → isVisitCompleted
// so the table row's "completed" treatment matches the sidebar card.
function isScheduleCompleted(schedule, now = new Date()) {
  if (!schedule) return false;
  const transcripts = schedule.transcripts;
  const moreAudios = schedule.more_audios;
  const hasRecording =
    (Array.isArray(transcripts) && transcripts.length > 0) ||
    (Array.isArray(moreAudios) && moreAudios.length > 0);
  if (!hasRecording) return false;
  const date = schedule.date_;
  const rawTime = schedule.time_ || schedule.appointment_time;
  if (!date || !rawTime) return false;
  let time = String(rawTime).replace(/[+\-Z].*$/, '');
  if (time.length === 5) time += ':00';
  const dt = new Date(`${date}T${time}`);
  if (Number.isNaN(dt.getTime())) return false;
  return dt <= now;
}

// Helper function to get user's schema - uses single source of truth from supabaseClient
const getUserSchema = async () => {
  return supabaseSchema || "public";
};

// Function to determine if a form is empty or filled based on URL pattern
const categorizeFormType = (url) => {
  if (!url) return null;
  // Empty forms have UUID only (like: /741e9531-9dbe-46d9-82b7-b157a78770ff)
  // Filled forms have /submission/ in the path (like: /9cd2ebc0-1406-4c5a-8655-8f06c6e5bade/submission/229a3689-4b85-4bb1-b81b-bcc76404e039)
  return url.includes("/submission/") ? "filled" : "empty";
};

// Function to determine button color scheme based on form types
const getButtonColorScheme = (formTypes) => {
  if (!formTypes || formTypes.length === 0) return "blue";

  const hasEmpty = formTypes.includes("empty");
  const hasFilled = formTypes.includes("filled");

  if (hasEmpty && hasFilled) {
    return "blue"; // Mixed - will use gradient
  } else if (hasEmpty) {
    return "lightblue"; // Only empty forms
  } else if (hasFilled) {
    return "blue"; // Only filled forms
  }

  return "blue"; // Default
};

// Function to get button styling based on form types
const getButtonStyling = (formTypes) => {
  if (!formTypes || formTypes.length === 0) {
    return { colorScheme: "blue", style: {} };
  }

  const hasEmpty = formTypes.includes("empty");
  const hasFilled = formTypes.includes("filled");

  if (hasEmpty && hasFilled) {
    // Mixed forms - use gradient
    return {
      colorScheme: "blue",
      style: {
        background: `linear-gradient(90deg, ${filled_button_form} 50%, ${empty_button_form} 50%)`,
        color: "white",
        border: "1px solid #2B6CB0",
        fontWeight: "500",
      },
    };
  } else if (hasEmpty) {
    // Only empty forms
    return {
      colorScheme: "blue",
      style: {
        backgroundColor: empty_button_form,
        color: "white",
        border: `1px solid ${empty_button_form}`,
        fontWeight: "500",
      },
    };
  } else if (hasFilled) {
    // Only filled forms
    return {
      colorScheme: "blue",
      style: {
        backgroundColor: filled_button_form,
        color: "white",
        border: `1px solid ${filled_button_form}`,
        fontWeight: "500",
      },
    };
  }

  return { colorScheme: "blue", style: {} };
};

// Function to format DOB from yyyy-mm-dd to mm/dd/yyyy
const formatDOB = (dobValue) => {
  if (!dobValue) return "N/A";

  // Check if it's already in mm/dd/yyyy format
  if (dobValue.includes("/")) {
    return dobValue;
  }

  // Check if it's in yyyy-mm-dd format
  if (dobValue.includes("-")) {
    try {
      // Parse the date components directly to avoid timezone issues
      const [year, month, day] = dobValue.split("-");
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));

      if (isNaN(date.getTime())) {
        return dobValue; // Return original if invalid date
      }

      return date.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      });
    } catch (error) {
      console.warn("Error formatting DOB:", error);
      return dobValue; // Return original if error
    }
  }

  return dobValue; // Return original if not in expected format
};

// An id PostgREST can match against the uuid `id` column. One id that is not
// fails a whole `.in("id", ids)` read, so the batched submission-links read
// leaves such rows out (the per-row lookup it replaced found none for them).
const UUID_COLUMN_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// `results` often arrives as a new array holding the very same row objects:
// SchedulesTable passes `visibleSchedules.slice(...)` inline, PatientPage a
// slice of its first three visits, a few callers an inline `[]`. Effects keyed
// on that array re-ran their reads on every parent render. This keeps the
// previous array while the rows are the same objects in the same order.
function useStableRows(rows) {
  const ref = useRef(rows);
  const prev = ref.current;
  const same =
    prev === rows ||
    (Array.isArray(prev) &&
      Array.isArray(rows) &&
      prev.length === rows.length &&
      prev.every((row, i) => row === rows[i]));
  if (!same) ref.current = rows;
  return ref.current;
}

const ResultsTable = ({
  results,
  setResults,
  prompts,
  hiddenColumns = [], // Array of column names to hide (e.g., ['tel_num', 'submissions_array'])
  userRole = null, // User role for displaying role-specific column labels
  selectedRows,
  handleCheckboxChange,
  handleViewJSONFiles,
  handleViewFolderFiles,
  currentFolderPath,
  previousFolderPath,
  handleGoUpOneLevel,
  handleGoDownOneLevel,
  bucketName_files,
  modalIsOpen,
  setModalIsOpen,
  currentFolderFiles,
  refreshCurrentFolderFiles,
  showCheckboxes = true,
  showFiles = true,
  // Search page: make the whole results row clickable so it opens that row's
  // document (chart) page — the same destination as the patient-name link.
  // Opt-in so other callers (schedules, RecentAudios, uploaders, …) keep
  // row-click = select. Clicks on controls (name link, buttons, checkboxes)
  // are ignored so they keep their own behavior.
  navigateOnRowClick = false,
  schedules,
  activePatients = new Set(),
  activeDocuments = [],
  schedulesDeps,
  activeDisplayOrder,
  // Cross-schema providers: the viewer's own schema. When a result row carries a
  // different `_schema` (a cross-schema search hit), its name cell gets a
  // provider badge. Default null → no badge (single-schema / non-cross callers).
  homeSchema = null,
  isAdmin,
  isNotAdmin,
  schedulesWithFiles = new Set(),
  filesCheckComplete = false,
  // Results (non-schedules) table: which rows' report folders actually hold
  // something, and whether that check has finished. /search supplies them
  // because `reports` is only a pointer there — the roster build stamps one on
  // every patient record. Callers that pass nothing keep rendering the buttons
  // off the pointer alone.
  reportFoldersWithFiles,
  reportFilesChecked = false,
  onViewForms,
  onViewMessages,
  processingFiles = {}, // Track files being processed: { filePath: { status: 'uploading'|'classifying'|'extracting', message: string } }
  onProcessReport, // Optional: callback(filePath, fileName, reportType) for processing qualifying reports
  viewerTimezone = "America/New_York",
  inlineExplorerMountNode = null,
  showInlineReportsUpload = false,
  suppressReportsFolderNav = false,
  suppressInlineFileActions = false,
  onInlineFilePreview = null,
  // When true, only render the modals (View Reports, Rename, Reassign,
  // Extract Text, Truncation, Change Classification, Password). The main
  // schedules table is skipped. Used by the global Reports bubble host so
  // the same modal logic powers the floating-reports popup.
  globalModalOnly = false,
}) => {
  rLog("[ResultsTable] Component rendered with userRole:", userRole);

  // Get role-based column labels
  const columnLabels = userRole ? getColumnLabels(userRole) : {};
  rLog("[ResultsTable] Column labels for role:", columnLabels);

  // Helper function to get the display label for a column
  const getColumnLabel = (promptName, defaultLabel) => {
    if (columnLabels[promptName]) {
      rLog(
        `[ResultsTable] Using custom label for ${promptName}:`,
        columnLabels[promptName],
      );
      return columnLabels[promptName];
    }
    rLog(
      `[ResultsTable] Using default label for ${promptName}:`,
      defaultLabel,
    );
    return defaultLabel;
  };

  // The View JSON Files / View Reports cells of the results table. Both read
  // the same `reports` pointer, so both are gated the same way.
  const reportsCell = (folder, label) => {
    const state = reportCellState(folder, reportFoldersWithFiles, reportFilesChecked);
    if (state === "button") {
      return <Button onClick={() => handleViewFolderFiles(folder)}>{label}</Button>;
    }
    return state === "pending" ? null : "N/A";
  };

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [emailToShare, setEmailToShare] = useState("");
  const [selectedFileId, setSelectedFileId] = useState(null);
  const [audio, setAudio] = useState(undefined);
  const [allAudios, setAllAudios] = useState([]);
  const [isAudioPlayerModalOpen, setisAudioPlayerModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [defaultSort] = useState(results);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);
  const [savedTranscriptionsCount, setSavedTranscriptionsCount] = useState({});
  const [idsWithTranscripts, setIdsWithTranscripts] = useState(new Set());
  const [expandedForms, setExpandedForms] = useState(new Set());

  // Write-only since the Live Transcription column was replaced by Orders: the
  // per-patient setting still lives in localStorage and is read by RecordingPage,
  // there is just no longer a checkbox here to render it. Kept rather than deleted
  // because the effect that fills it also fills selectedForms, which the results
  // table below still uses — untangling that is a bigger change than this one.
  // eslint-disable-next-line no-unused-vars
  const [selectedLiveTranscription, setSelectedLiveTranscription] = useState(
    {},
  );

  // Password modal state
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  // State for submission links
  const [submissionLinks, setSubmissionLinks] = useState([]);

  // State for unread messages per patient (only used in AdminPage)
  const [unreadMessages, setUnreadMessages] = useState({}); // { patientId: count }

  // State for file classifications (for color coding)
  const [fileClassifications, setFileClassifications] = useState({});
  // filePath -> boolean. Deliberately a PARALLEL map rather than a new shape for
  // fileClassifications: that map is read as a bare string in several places
  // (e.g. classifyReport(), getMetadataColor()), so widening it to an object
  // would silently break colour coding. is_order is orthogonal to classification —
  // a lab result annotated "repeat in 3 months" is both Labs AND an order.
  const [fileIsOrder, setFileIsOrder] = useState({});
  // False from the moment a folder's files change until its classifications
  // have been fetched. Rendering the list before then shows every file as
  // unclassified and NOT an order, so orders flash into `All` and vanish.
  const [classificationsReady, setClassificationsReady] = useState(false);
  const [fileMetadata, setFileMetadata] = useState({});
  const [filesWithExtractedText, setFilesWithExtractedText] = useState(
    new Set(),
  ); // Track which files have extracted text
  const [fileSourceInfo, setFileSourceInfo] = useState({}); // Track fax vs report with timestamps
  const [expandedSubmissionId, setExpandedSubmissionId] = useState(null);
  const [linkCounts, setLinkCounts] = useState({});
  const [formTypes, setFormTypes] = useState({});

  // State for file selection in modal
  const [selectedFiles, setSelectedFiles] = useState(new Set());

  // State for rename and reassign functionality
  const [selectedFileForRename, setSelectedFileForRename] = useState(null);
  const [newFileName, setNewFileName] = useState("");
  const [renamingFile, setRenamingFile] = useState(false);
  const [selectedFileForReassign, setSelectedFileForReassign] = useState(null);
  const [selectedPatientForReassign, setSelectedPatientForReassign] =
    useState(null);
  const [reassigningFile, setReassigningFile] = useState(false);
  const [selectedFileForClassification, setSelectedFileForClassification] =
    useState(null);
  const [newClassification, setNewClassification] = useState("");
  const [newIsOrder, setNewIsOrder] = useState(false);
  const [changingClassification, setChangingClassification] = useState(false);
  const [extractingText, setExtractingText] = useState(false);
  const [viewingTextData, setViewingTextData] = useState(null); // { file_path, filename, full_text }
  const [isReExtractingText, setIsReExtractingText] = useState(false);
  const inlineReportsUploadInputRef = useRef(null);
  const [isInlineReportsUploading, setIsInlineReportsUploading] =
    useState(false);
  const isReportsExplorerVisible = modalIsOpen || Boolean(inlineExplorerMountNode);

  // ---------------------------------------------------------------------------
  // View Reports filter chips.
  //
  // HOISTED HERE ON PURPOSE — do not move this into renderViewReportsExplorerBody.
  // That function is a plain helper called during render, not a component, and it
  // is called a VARIABLE number of times per render:
  //   - once unconditionally as the DraggableResizableModal's child, and
  //   - a second time, only when `inlineExplorerMountNode && !modalIsOpen`, for
  //     the inline portal.
  // Hooks called inside it would therefore appear once or twice depending on
  // modalIsOpen, which breaks the rules of hooks ("Rendered fewer hooks than
  // expected" the moment the modal opens). Calling useReportFilters once here in
  // the component body keeps the hook order fixed; both render sites read the
  // result off this closure. They also share one filter selection, which is what
  // we want — the two surfaces are mutually exclusive, so only one is ever shown.
  const reportFilterEntries = useMemo(
    () =>
      (currentFolderFiles || [])
        // Count reports only. Folders are navigation and are never filtered out,
        // so including them would inflate "Unclassified" with subfolder rows.
        .filter((file) => REPORT_FILE_RE.test(file.name))
        .map((file) => {
          const p = currentFolderPath
            ? `${currentFolderPath}/${file.name}`
            : file.name;
          return {
            classification: fileClassifications[p] ?? null,
            is_order: !!fileIsOrder[p],
          };
        }),
    [currentFolderFiles, currentFolderPath, fileClassifications, fileIsOrder],
  );
  const {
    activeFilter: reportActiveFilter,
    setActiveFilter: setReportActiveFilter,
    counts: reportFilterCounts,
    visibleFilters: reportVisibleFilters,
    matches: reportMatches,
  } = useReportFilters(reportFilterEntries);

  // Truncation confirmation state
  const [truncationInfo, setTruncationInfo] = useState(null); // { file_path, filename, total_pages, pages_processed, remaining_pages }
  const [isContinuingExtraction, setIsContinuingExtraction] = useState(false);
  const [continueProgress, setContinueProgress] = useState(null);

  // Helper functions for localStorage truncation tracking
  const TRUNCATION_STORAGE_KEY = "pending_truncations";
  const DECLINED_TRUNCATION_KEY = "declined_truncations";

  const getPendingTruncations = () => {
    try {
      const stored = localStorage.getItem(TRUNCATION_STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch (e) {
      console.error("Error reading truncation storage:", e);
      return {};
    }
  };

  const getDeclinedTruncations = () => {
    try {
      const stored = localStorage.getItem(DECLINED_TRUNCATION_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch (e) {
      console.error("Error reading declined truncation storage:", e);
      return {};
    }
  };

  const savePendingTruncation = (filePath, truncationData) => {
    try {
      const pending = getPendingTruncations();
      pending[filePath] = truncationData;
      localStorage.setItem(TRUNCATION_STORAGE_KEY, JSON.stringify(pending));
      rLog(
        "Saved pending truncation to localStorage:",
        filePath,
        truncationData,
      );
    } catch (e) {
      console.error("Error saving truncation storage:", e);
    }
  };

  const saveDeclinedTruncation = (filePath) => {
    try {
      const declined = getDeclinedTruncations();
      declined[filePath] = true;
      localStorage.setItem(DECLINED_TRUNCATION_KEY, JSON.stringify(declined));
      rLog("Saved declined truncation to localStorage:", filePath);
    } catch (e) {
      console.error("Error saving declined truncation storage:", e);
    }
  };

  const hasDeclinedTruncation = (filePath) => {
    const declined = getDeclinedTruncations();
    return !!declined[filePath];
  };

  const clearPendingTruncation = (filePath) => {
    try {
      const pending = getPendingTruncations();
      delete pending[filePath];
      localStorage.setItem(TRUNCATION_STORAGE_KEY, JSON.stringify(pending));
      rLog("Cleared pending truncation from localStorage:", filePath);
    } catch (e) {
      console.error("Error clearing truncation storage:", e);
    }
  };

  const hasPendingTruncation = (filePath) => {
    const pending = getPendingTruncations();
    return !!pending[filePath];
  };

  // Track previous processingFiles to detect when files finish processing
  const prevProcessingFilesRef = useRef({});

  const {
    isOpen: isRenameFileOpen,
    onOpen: onRenameFileOpen,
    onClose: onRenameFileClose,
  } = useDisclosure();

  const {
    isOpen: isViewTextModalOpen,
    onOpen: onViewTextModalOpen,
    onClose: onViewTextModalClose,
  } = useDisclosure();

  const {
    isOpen: isTruncationModalOpen,
    onOpen: onTruncationModalOpen,
    onClose: onTruncationModalClose,
  } = useDisclosure();

  const {
    isOpen: isReassignFileOpen,
    onOpen: onReassignFileOpen,
    onClose: onReassignFileClose,
  } = useDisclosure();

  const {
    isOpen: isChangeClassificationOpen,
    onOpen: onChangeClassificationOpen,
    onClose: onChangeClassificationClose,
  } = useDisclosure();

  // Note: ReactModal doesn't lock body scrolling, so no need for scroll lock prevention

  // Handle extract text and metadata
  const handleExtractTextMetadata = async (filePath, fileName) => {
    if (!filePath) {
      toast({
        title: "Error",
        description: "File path is required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setExtractingText(true);
    try {
      const { BASE_URLS } = await import("../env");
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      const response = await fetch(
        `${baseUrl}/api/fax/extract-text-metadata/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ file_paths: [filePath] }),
        },
      );

      if (!response.ok) {
        if (response.status === 502 || response.status === 503) {
          throw new Error(
            "Server ran out of memory on Render. Please try again or upgrade the Render plan.",
          );
        }
        let errorMessage = "Failed to extract text and metadata";
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (parseError) {
          // Response body is not JSON (e.g. HTML error page from proxy)
          errorMessage = `Server error (${response.status}). The server may have run out of memory on Render.`;
        }
        throw new Error(errorMessage);
      }

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        throw new Error(
          "Server ran out of memory on Render. The response was cut off mid-extraction. Please try again or upgrade the Render plan.",
        );
      }
      rLog("Extraction response:", data);

      // Check if any files were truncated
      if (
        data.has_truncated_files &&
        data.truncated_files &&
        data.truncated_files.length > 0
      ) {
        const truncatedFile = data.truncated_files[0]; // Handle first truncated file
        rLog("Document was truncated:", truncatedFile);

        const truncationData = {
          file_path: truncatedFile.file_path,
          filename: truncatedFile.filename,
          total_pages: truncatedFile.total_pages,
          pages_processed: truncatedFile.pages_processed,
          remaining_pages: truncatedFile.remaining_pages,
        };

        // Save to localStorage so it persists until user makes a choice
        savePendingTruncation(truncatedFile.file_path, truncationData);

        setTruncationInfo(truncationData);
        onTruncationModalOpen();

        // Still show success but mention truncation
        toast({
          title: "Extraction Complete",
          description: `Processed first ${truncatedFile.pages_processed} of ${truncatedFile.total_pages} pages. ${truncatedFile.remaining_pages} pages remaining.`,
          status: "info",
          duration: 7000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Success",
          // Deliberately NOT data.message: the /api/fax/extract-text-metadata/
          // endpoint is shared with the fax UI and phrases its message as
          // "N fax(es)". In the reports modal these are reports, not faxes.
          description: `Extracted text and metadata for ${data.updated_count} report(s)`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      }

      // Update extracted text status immediately
      setFilesWithExtractedText((prev) => {
        const updated = new Set(prev);
        updated.add(filePath);
        return updated;
      });

      // Refresh file classifications and metadata
      if (refreshCurrentFolderFiles) {
        refreshCurrentFolderFiles();
      }
    } catch (error) {
      console.error("Error extracting text/metadata:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to extract text and metadata",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setExtractingText(false);
    }
  };

  // Handle continue extraction for truncated documents
  const handleContinueExtraction = async () => {
    if (!truncationInfo) return;

    setIsContinuingExtraction(true);
    setContinueProgress(null);
    try {
      const { BASE_URLS } = await import("../env");
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      // The server OCRs a batch of pages per request; call until none remain.
      const data = await continueOcrExtractionInBatches(async () => {
        const response = await fetch(
          `${baseUrl}/api/fax/continue-ocr-extraction/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ file_path: truncationInfo.file_path }),
          },
        );

        if (!response.ok) {
          if (response.status === 502 || response.status === 503) {
            throw new Error(
              "Server ran out of memory on Render. Please try again or upgrade the Render plan.",
            );
          }
          let errorMessage = "Failed to continue extraction";
          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorMessage;
          } catch (parseError) {
            errorMessage = `Server error (${response.status}). The server may have run out of memory on Render.`;
          }
          throw new Error(errorMessage);
        }

        try {
          return await response.json();
        } catch (parseError) {
          throw new Error(
            "Server ran out of memory on Render. The response was cut off mid-extraction. Please try again or upgrade the Render plan.",
          );
        }
      }, { onProgress: setContinueProgress });

      // Pages left (the OCR service stopped responding mid-way): keep the
      // pending entry so the prompt comes back, and say what was saved.
      const incomplete = continueOcrIncompleteToast(data);
      if (incomplete) {
        // Keep the entry, with the counts that are left now.
        savePendingTruncation(
          truncationInfo.file_path,
          continueOcrUpdatedTruncation(truncationInfo, data),
        );
        toast({ ...incomplete, duration: 8000, isClosable: true });
      } else {
        // Clear from localStorage since user made a choice
        clearPendingTruncation(truncationInfo.file_path);

        toast({
          title: "Success",
          description: data.message || `Successfully processed remaining pages`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      }

      // Refresh file classifications and metadata
      if (refreshCurrentFolderFiles) {
        refreshCurrentFolderFiles();
      }

      onTruncationModalClose();
      setTruncationInfo(null);
    } catch (error) {
      console.error("Error continuing extraction:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to continue extraction",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsContinuingExtraction(false);
      setContinueProgress(null);
    }
  };

  // Handle declining to continue extraction
  const handleDeclineContinueExtraction = () => {
    if (truncationInfo) {
      // Clear from pending and mark as declined so we don't show the modal again
      clearPendingTruncation(truncationInfo.file_path);
      saveDeclinedTruncation(truncationInfo.file_path);
    }
    onTruncationModalClose();
    setTruncationInfo(null);
    toast({
      title: "Extraction Complete",
      description: "Document was processed with the first 10 pages only.",
      status: "info",
      duration: 5000,
      isClosable: true,
    });
  };

  // Handle viewing extracted text from truncation modal
  const handleViewTextFromTruncationModal = async () => {
    if (!truncationInfo) return;

    try {
      const supabase = await getSupabase();
      const { data, error } = await supabase
        .from("file_classification_cache")
        .select("full_text, filename, updated_at")
        .eq("file_path", truncationInfo.file_path)
        .single();

      if (error) {
        throw new Error(error.message || "Failed to fetch extracted text");
      }

      if (!data || !data.full_text) {
        toast({
          title: "No Text Available",
          description:
            "This file does not have extracted text in the database.",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      setViewingTextData({
        file_path: truncationInfo.file_path,
        filename: data.filename || truncationInfo.filename || "Unknown",
        full_text: data.full_text,
        updated_at: data.updated_at,
      });
      // Keep truncation modal open, just open the view text modal alongside
      onViewTextModalOpen();
    } catch (error) {
      console.error("Error fetching extracted text:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to fetch extracted text",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Handle view extracted text
  const handleViewExtractedText = async (filePath, fileName) => {
    if (!filePath) {
      toast({
        title: "Error",
        description: "File path is required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Check if user has already declined truncation for this file
    if (hasDeclinedTruncation(filePath)) {
      rLog("User already declined truncation for file:", filePath);
      // Skip truncation modal, go straight to viewing text
    } else {
      // Check if there's a pending truncation for this file
      const pendingTruncations = getPendingTruncations();
      const pendingTruncation = pendingTruncations[filePath];

      if (pendingTruncation) {
        rLog(
          "Found pending truncation for file:",
          filePath,
          pendingTruncation,
        );
        // Show truncation modal first
        setTruncationInfo(pendingTruncation);
        onTruncationModalOpen();
        return; // Don't open view text modal until user makes a choice
      }
    }

    try {
      const supabase = await getSupabase();
      // file_classification_cache is PER SCHEMA, and the owning schema is the
      // one in the file's path — not the viewer's active schema. Cross-schema
      // providers (and the fax schema-group fan-out) routinely put a chart's
      // files under a different schema than the one the user is browsing, and
      // the unqualified read used to find nothing and report "No Extracted Text
      // Found" for a row that was there.
      //
      // maybeSingle(), not single(): a miss must be an empty result we can
      // retry in the next candidate schema, not an HTTP 406.
      let data = null;
      let error = null;
      for (const schema of cacheSchemaCandidates(filePath, supabaseSchema)) {
        const res = await clientForSchema(supabase, schema, supabaseSchema)
          .from("file_classification_cache")
          .select("full_text, filename, updated_at, metadata")
          .eq("file_path", filePath)
          .maybeSingle();
        if (res.data) {
          data = res.data;
          error = null;
          break;
        }
        error = res.error || null;
      }
      // Preserve the "nothing found" contract the handler below expects.
      if (!data && !error) error = { code: "PGRST116" };

      if (error) {
        if (
          error.code === "PGRST116" ||
          (error.message || "").includes("multiple (or no) rows")
        ) {
          toast({
            title: "No Extracted Text Found",
            description:
              "No extracted text record exists for this file yet. Try re-extracting the file first.",
            status: "warning",
            duration: 5000,
            isClosable: true,
          });
          return;
        }
        throw new Error(error.message || "Failed to fetch extracted text");
      }

      if (!data || !data.full_text) {
        toast({
          title: "No Text Available",
          description:
            "This file does not have extracted text in the database.",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // Also check if metadata has truncation info (for files extracted before localStorage was implemented)
      // But skip if user has already declined
      if (
        data.metadata?.truncation_info?.was_truncated &&
        !hasDeclinedTruncation(filePath)
      ) {
        const truncInfo = data.metadata.truncation_info;
        const truncationData = {
          file_path: filePath,
          filename: data.filename || fileName || "Unknown",
          total_pages: truncInfo.total_pages,
          pages_processed: truncInfo.pages_processed,
          remaining_pages: truncInfo.remaining_pages,
        };
        rLog("Found truncation info in metadata:", truncationData);
        // Save to localStorage and show modal
        savePendingTruncation(filePath, truncationData);
        setTruncationInfo(truncationData);
        onTruncationModalOpen();
        return;
      }

      setViewingTextData({
        file_path: filePath,
        filename: data.filename || fileName || "Unknown",
        full_text: data.full_text,
        updated_at: data.updated_at,
      });
      onViewTextModalOpen();
    } catch (error) {
      console.error("Error fetching extracted text:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to fetch extracted text",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Handle re-extract text from the extracted text modal
  const handleReExtractText = async () => {
    if (!viewingTextData?.file_path) return;

    setIsReExtractingText(true);
    try {
      const { BASE_URLS } = await import("../env");
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      const response = await fetch(
        `${baseUrl}/api/fax/extract-text-metadata/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ file_paths: [viewingTextData.file_path] }),
        },
      );

      if (!response.ok) {
        if (response.status === 502 || response.status === 503) {
          throw new Error(
            "Server ran out of memory on Render. Please try again or upgrade the Render plan.",
          );
        }
        let errorMessage = "Failed to re-extract text and metadata";
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (parseError) {
          errorMessage = `Server error (${response.status}). The server may have run out of memory on Render.`;
        }
        throw new Error(errorMessage);
      }

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        throw new Error(
          "Server ran out of memory on Render. The response was cut off mid-extraction. Please try again or upgrade the Render plan.",
        );
      }

      rLog("Re-extraction response:", data);

      // Check if any files were truncated
      if (
        data.has_truncated_files &&
        data.truncated_files &&
        data.truncated_files.length > 0
      ) {
        const truncatedFile = data.truncated_files[0];
        rLog("Re-extracted document was truncated:", truncatedFile);

        const truncationData = {
          file_path: truncatedFile.file_path,
          filename: truncatedFile.filename,
          total_pages: truncatedFile.total_pages,
          pages_processed: truncatedFile.pages_processed,
          remaining_pages: truncatedFile.remaining_pages,
        };

        // Save to localStorage so it persists until user makes a choice
        savePendingTruncation(truncatedFile.file_path, truncationData);

        setTruncationInfo(truncationData);
        onTruncationModalOpen();

        toast({
          title: "Extraction Complete",
          description: `Processed first ${truncatedFile.pages_processed} of ${truncatedFile.total_pages} pages. ${truncatedFile.remaining_pages} pages remaining.`,
          status: "info",
          duration: 7000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Success",
          // Not data.message — see the note on the other extract call site: the
          // shared fax endpoint phrases its message as "N fax(es)".
          description: `Re-extracted text and metadata successfully`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      }

      // Refresh the extracted data in the modal
      const supabase = await getSupabase();
      const { data: refreshedData, error: refreshError } = await supabase
        .from("file_classification_cache")
        .select("full_text, filename, updated_at")
        .eq("file_path", viewingTextData.file_path)
        .single();

      if (!refreshError && refreshedData) {
        setViewingTextData({
          ...viewingTextData,
          full_text: refreshedData.full_text || null,
          filename: refreshedData.filename || viewingTextData.filename,
          updated_at: refreshedData.updated_at || null,
        });
      }

      // Update filesWithExtractedText to include this file
      setFilesWithExtractedText((prev) => {
        const newSet = new Set(prev);
        newSet.add(viewingTextData.file_path);
        return newSet;
      });
    } catch (error) {
      console.error("Error re-extracting text/metadata:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to re-extract text and metadata",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsReExtractingText(false);
    }
  };

  // The rows the per-row effects below act on, as they always have
  // (`schedules || results`), but with `results` stabilised (useStableRows)
  // so a parent render that re-slices the same rows does not re-run them.
  const stableResults = useStableRows(results);
  const tableRows = schedules || stableResults;

  // Link counts and form types, from ONE read per schema. Form types used to
  // be looked up row by row (extractAllSubmissionLinks awaited in a loop) in
  // two identical effects that re-ran on every render: ~13 s of serial
  // requests each on a 92-row day. The counts read now also selects the
  // single-link column, and submissionLinksFromRow gives each row the links
  // that lookup returned.
  useEffect(() => {
    let cancelled = false;
    const fetchAllCounts = async () => {
      const data = tableRows;
      if (!data || data.length === 0) return;

      rLog("🔍 Pre-fetching form types for all items...");

      // Group ids by their source schema so cross-schema (linked-provider)
      // patients are read from the right PostgREST schema instead of the
      // viewer's home schema (where their row doesn't exist).
      const bySchema = new Map();
      for (const s of data) {
        if (typeof s.id !== "string" || !UUID_COLUMN_RE.test(s.id)) continue;
        const schema = s._schema || supabaseSchema || "public";
        if (!bySchema.has(schema)) bySchema.set(schema, []);
        bySchema.get(schema).push(s.id);
      }

      // schema -> (id -> row as read). A schema whose read fails gets no
      // entry: its rows have no stored links (the per-row lookup answered []
      // on an error too) and, as before, no counts are set at all.
      const readBySchema = new Map();
      let countsError = null;
      try {
        const supabase = await getSupabase();
        const responses = await Promise.all(
          Array.from(bySchema.entries()).map(async ([schema, ids]) => {
            try {
              const { data: rows, error } = await supabase
                .schema(schema)
                .from(tableName)
                .select(
                  `id, ${submissions_arrayUrls}, ${singleLinkSubmissionUrl}`,
                )
                .in("id", ids);
              return { schema, rows, error };
            } catch (error) {
              return { schema, rows: null, error };
            }
          }),
        );

        responses.forEach(({ schema, rows, error }) => {
          if (error) {
            countsError = countsError || error;
            return;
          }
          readBySchema.set(
            schema,
            new Map((rows || []).map((row) => [row.id, row])),
          );
        });
      } catch (error) {
        countsError = error;
      }
      if (cancelled) return;

      // Counts stay a schedules-view feature, as before.
      if (schedules) {
        if (countsError) {
          console.error("Error fetching submission counts:", countsError);
        } else {
          const newCounts = {};
          readBySchema.forEach((rowsById) => {
            rowsById.forEach((item) => {
              if (Array.isArray(item.submissions_array)) {
                newCounts[item.id] = item.submissions_array.length;
              } else if (item.submissions_array) {
                newCounts[item.id] = 1;
              } else {
                newCounts[item.id] = 0;
              }
            });
          });
          setLinkCounts(newCounts);
        }
      }

      const newFormTypes = {};

      for (const item of data) {
        try {
          const schema = item._schema || supabaseSchema || "public";
          let linksToDisplay = submissionLinksFromRow(
            item.id,
            readBySchema.get(schema)?.get(item.id),
          );

          // If the array is empty, use the fallback URL
          if (linksToDisplay.length === 0 && item.submission_url) {
            linksToDisplay = [item.submission_url];
          }

          // Categorize form types and store them
          const types = linksToDisplay.map((link) => categorizeFormType(link));
          newFormTypes[item.id] = types;

          rLog(`📋 Item ${item.id}:`, {
            links: linksToDisplay,
            types: types,
          });
        } catch (error) {
          console.error(
            `Error fetching form types for item ${item.id}:`,
            error,
          );
          newFormTypes[item.id] = [];
        }
      }

      setFormTypes(newFormTypes);
      rLog(
        "✅ All form types pre-fetched and categorized:",
        newFormTypes,
      );
    };

    fetchAllCounts();
    return () => {
      cancelled = true;
    };
  }, [schedules, tableRows]);

  // Function to get form types for a schedule/result
  const getFormTypesForItem = async (item) => {
    try {
      let linksToDisplay = await extractAllSubmissionLinks(
        item.id,
        item._schema,
      );

      // If the array is empty, use the fallback URL
      if (linksToDisplay.length === 0 && item.submission_url) {
        linksToDisplay = [item.submission_url];
      }

      return linksToDisplay.map((link) => categorizeFormType(link));
    } catch (error) {
      console.error("Error fetching form types:", error);
      return [];
    }
  };

  // Efficiently fetch all patient IDs with a transcript in the database.
  // Gates the "T" button. Rows merged in from a linked clinic carry `_schema`
  // and live in THAT schema's table, so group the lookups by schema (one
  // batched query each) exactly like the submission-count effect above —
  // reading them through the viewer's home client found no row and silently
  // hid the button for shared patients while their audio still showed.
  useEffect(() => {
    const fetchIdsWithTranscripts = async () => {
      try {
        const supabase = await getSupabase();
        const transcriptIds = new Set();

        // Add null check for schedules to prevent "not iterable" error
        if (!schedules || !Array.isArray(schedules)) {
          rLog("📊 No schedules data available for transcript fetching");
          return;
        }

        const bySchema = new Map();
        for (const schedule of schedules) {
          if (!schedule?.id) continue;
          const schema = schedule._schema || supabaseSchema || "public";
          if (!bySchema.has(schema)) bySchema.set(schema, []);
          bySchema.get(schema).push(schedule);
        }

        const responses = await Promise.all(
          Array.from(bySchema.entries()).map(async ([schema, rows]) => {
            const { data, error } = await supabase
              .schema(schema)
              .from(tableName)
              .select("id, transcripts")
              .in("id", rows.map((r) => r.id));
            if (error) throw error;
            return { schema, rows, data: data || [] };
          }),
        );

        for (const { schema, rows, data } of responses) {
          const transcriptsById = new Map(data.map((d) => [d.id, d.transcripts]));
          for (const schedule of rows) {
            const raw = transcriptsById.get(schedule.id);
            const rawList = raw ? (Array.isArray(raw) ? raw : [raw]) : [];
            const visitTranscripts = filterTranscriptsForVisitDate(
              rawList,
              schedule.date_,
              schedule.more_audios || [],
              schema,
            );
            if (visitTranscripts.some((t) => !t.is_ai_recap)) {
              transcriptIds.add(schedule.id);
            }
          }
        }
        setIdsWithTranscripts(transcriptIds);
      } catch (error) {
        console.error("Error fetching transcript flags from Supabase:", error);
      }
    };
    fetchIdsWithTranscripts();
  }, [schedules]);

  // State for Forms column checkboxes
  const [selectedForms, setSelectedForms] = useState({});
  const navigate = useNavigate();
  // The router's basename ("/NotesAI" locally) as a real href prefix, for
  // opening a row's chart in a NEW tab: window.open takes a URL, not a
  // router path, and a new tab starts with no router state — the chart page
  // reads the visit from the URL's id/date/schema alone.
  const rootHref = useHref("/");
  const openVisitInNewTab = (row) => {
    const nav = visitNavFor(row, userRole);
    if (!nav) return false;
    window.open(newTabHref(rootHref, nav.to), "_blank", "noopener");
    return true;
  };
  const toast = useToast({
    containerStyle: {
      zIndex: 99999, // Ensure toasts appear above modals (which have z-index 9999-10004)
    },
  });

  // Check for saved transcriptions for each patient
  useEffect(() => {
    const checkSavedTranscriptions = () => {
      try {
        const existingData = localStorage.getItem("saved_transcriptions");
        const allSavedTranscriptions = existingData
          ? JSON.parse(existingData)
          : [];

        const counts = {};
        // Add null check for schedules
        if (schedules && Array.isArray(schedules)) {
          schedules.forEach((schedule) => {
            const patientTranscriptions = allSavedTranscriptions.filter(
              (transcription) => transcription.patient_id === schedule.id,
            );
            counts[schedule.id] = patientTranscriptions.length;
          });
        }

        setSavedTranscriptionsCount(counts);
      } catch (error) {
        console.error("Error checking saved transcriptions:", error);
      }
    };

    checkSavedTranscriptions();

    // Listen for custom transcription saved event
    const handleTranscriptionSaved = () => {
      checkSavedTranscriptions();
    };

    window.addEventListener("transcriptionSaved", handleTranscriptionSaved);

    return () => {
      window.removeEventListener(
        "transcriptionSaved",
        handleTranscriptionSaved,
      );
    };
  }, [schedules]);

  // Initialize selectedForms from schedules or results data. Keyed exactly
  // like the forms_ind read further down, which re-applies the database
  // values after this reset; left to run on every render without that read,
  // it would put a just-toggled box back to its row's stale value.
  useEffect(() => {
    const data = tableRows;
    if (data && data.length > 0) {
      rLog("📊 Initializing checkbox states with data:", data);
      const initialSelectedForms = {};
      const initialSelectedLiveTranscription = {};
      data.forEach((item) => {
        rLog(`📋 Item ${item.id}: forms_ind = ${item.forms_ind}`);

        let formsValue = item.forms_ind || false;
        let liveTranscriptionValue = getLiveTranscriptionSetting(item.id); // Use localStorage instead

        initialSelectedForms[item.id] = formsValue;
        initialSelectedLiveTranscription[item.id] = liveTranscriptionValue;
      });

      // Only log the final states once
      rLog(
        "🎯 Final live transcription states:",
        initialSelectedLiveTranscription,
      );
      setSelectedForms(initialSelectedForms);
      setSelectedLiveTranscription(initialSelectedLiveTranscription);
    }
  }, [tableRows]);

  // Listen for global live transcription setting changes
  useEffect(() => {
    const handleGlobalLiveTranscriptionChange = (event) => {
      rLog(
        "🌍 Global live transcription setting changed:",
        event.detail,
      );
      const { enabled } = event.detail;

      // Update all current checkboxes to match global setting
      const data = schedules || results;
      if (data && data.length > 0) {
        const newSelectedLiveTranscription = {};
        data.forEach((item) => {
          newSelectedLiveTranscription[item.id] = enabled;
        });
        setSelectedLiveTranscription(newSelectedLiveTranscription);
        rLog(
          "🌍 Updated selectedLiveTranscription state:",
          newSelectedLiveTranscription,
        );
      }
    };

    window.addEventListener(
      "globalLiveTranscriptionChanged",
      handleGlobalLiveTranscriptionChange,
    );

    return () => {
      window.removeEventListener(
        "globalLiveTranscriptionChanged",
        handleGlobalLiveTranscriptionChange,
      );
    };
  }, [schedules, results]);

  // The realtime handler below reads the current rows through this ref, so
  // its channel is joined once instead of left and re-joined every render.
  const tableRowsRef = useRef(tableRows);
  tableRowsRef.current = tableRows;

  // Real-time subscription for Supabase updates
  useEffect(() => {
    let cancelled = false;
    let activeChannel = null;

    const setupSubscription = async () => {
      try {
        const supabase = await getSupabase();
        if (cancelled) return;

        const channelSuffix =
          typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const channelId = `forms-changes:${tableName}:${channelSuffix}`;

        const ch = supabase
          .channel(channelId)
          .on(
            "postgres_changes",
            { event: "UPDATE", schema: "public", table: tableName },
            (payload) => {
              const updatedId = payload.new.id;
              const newFormsValue = payload.new.forms_ind;

              rLog(
                `Real-time update: id=${updatedId}, forms_ind=${newFormsValue}`,
              );
              if (
                tableRowsRef.current?.some((item) => item.id === updatedId)
              ) {
                setSelectedForms((prev) => ({
                  ...prev,
                  [updatedId]: newFormsValue,
                }));
              }
            },
          )
          .subscribe();

        if (cancelled) {
          ch.unsubscribe();
          return;
        }
        activeChannel = ch;
      } catch (error) {
        if (!cancelled) {
          console.error("Error setting up Supabase subscription:", error);
        }
      }
    };

    setupSubscription();

    return () => {
      cancelled = true;
      if (activeChannel) {
        activeChannel.unsubscribe();
        activeChannel = null;
      }
    };
  }, [tableName]);

  const ORDER_PATH_QUERY_LIMIT = 5000;

  // Which visible patients have at least one order-flagged report.
  //
  // One query for the whole page, not one per row: order reports are a small
  // slice of the cache, so we fetch their paths and match them against each
  // row's reports folder client-side. A per-row query would mean N round trips
  // for a table that routinely shows dozens of patients.
  const [patientsWithOrders, setPatientsWithOrders] = useState({});

  useEffect(() => {
    let cancelled = false;
    const fetchOrderFolders = async () => {
      if (!schedules || schedules.length === 0) {
        setPatientsWithOrders({});
        return;
      }
      try {
        const supabase = await getSupabase();
        const folders = scheduleFolders(
          schedules,
          file_reference_column,
          buildReportsFolder,
        );
        // ONE QUERY PER SCHEMA. `file_classification_cache` is per-schema and
        // the owner is encoded in the path, so a single unqualified read used
        // the viewer's active schema and found nothing for any patient whose
        // files live elsewhere — no Orders button, while the View Reports
        // modal (which resolves per path) listed the same orders happily.
        const schemas = schemasForFolders(folders);
        console.log(
          `[Orders] ${folders.length} row(s) with a reports folder across ` +
            `schema(s): ${schemas.join(", ") || "none"}`,
        );

        const perSchema = await Promise.all(
          schemas.map((schema) =>
            clientForSchema(supabase, schema, supabaseSchema)
              .from("file_classification_cache")
              .select("file_path")
              .eq("is_order", true)
              // PostgREST caps an unbounded select at 1000 rows; a clinic with
              // more orders than that would lose the tail silently.
              .limit(ORDER_PATH_QUERY_LIMIT)
              .then((res) => {
                if (res.error) {
                  // One unreadable schema must not blank the whole column.
                  console.warn(
                    `[Orders] schema "${schema}" query failed:`,
                    res.error,
                  );
                  return [];
                }
                const paths = (res.data || [])
                  .map((r) => r.file_path)
                  .filter(Boolean);
                console.log(
                  `[Orders] schema "${schema}": ${paths.length} order path(s)` +
                    (paths.length >= ORDER_PATH_QUERY_LIMIT
                      ? " — AT THE QUERY LIMIT, some may be missing"
                      : ""),
                );
                return paths;
              }),
          ),
        );

        if (cancelled) return;
        const orderPaths = perSchema.flat();
        const byPatient = matchOrdersToSchedules(folders, orderPaths);
        const matched = Object.keys(byPatient).length;
        console.log(
          `[Orders] ${orderPaths.length} order path(s) total → ` +
            `${matched} row(s) get the button`,
        );
        if (orderPaths.length > 0 && matched === 0) {
          // The interesting failure: orders exist but none sit under a
          // visible row's folder. Almost always a folder-shape mismatch.
          console.warn(
            "[Orders] orders were found but matched no row. Sample order path:",
            orderPaths[0],
            "| sample row folder:",
            folders[0]?.folder,
          );
        }
        setPatientsWithOrders(byPatient);
      } catch (e) {
        if (!cancelled) {
          console.warn("[Orders] Could not load order reports for schedules:", e);
          setPatientsWithOrders({});
        }
      }
    };
    fetchOrderFolders();
    return () => {
      cancelled = true;
    };
  }, [schedules]);

  // Fetch file classifications when folder files change
  useEffect(() => {
    const fetchFileClassifications = async () => {
      if (!currentFolderFiles || currentFolderFiles.length === 0) {
        setFileClassifications({});
        setFileIsOrder({});
        setClassificationsReady(true);
        return;
      }

      try {
        const supabase = await getSupabase();
        const classifications = {};

        // Build file paths and fetch classifications
        const filePaths = currentFolderFiles
          .filter((file) =>
            REPORT_FILE_RE.test(file.name),
          )
          .map((file) => {
            const path = currentFolderPath
              ? `${currentFolderPath}/${file.name}`
              : file.name;
            return { path, name: file.name };
          });

        if (filePaths.length === 0) {
          setFileClassifications({});
          setFileIsOrder({});
          return;
        }

        // Fetch classifications, metadata, full_text, and source info for all
        // files at once — but ONE QUERY PER SCHEMA. file_classification_cache
        // is per-schema and the owner is encoded in each path, so a single
        // unqualified .in() silently dropped every file living outside the
        // viewer's active schema: those rows rendered with no classification
        // chip, no "uploaded by", and no extracted-text indicator.
        const byPathSchema = groupPathsBySchema(filePaths.map((f) => f.path));
        const perSchema = await Promise.all(
          Object.entries(byPathSchema).map(([schema, paths]) =>
            clientForSchema(supabase, schema, supabaseSchema)
              .from("file_classification_cache")
              .select(
                "file_path, classification, is_order, metadata, full_text, is_faxed_report, created_at, uploaded_by_name",
              )
              .in("file_path", paths)
              .then((res) => {
                if (res.error) {
                  // One unreadable schema must not blank the whole table.
                  console.warn(
                    `Error fetching file classifications for schema "${schema}":`,
                    res.error,
                  );
                }
                return res.data || [];
              }),
          ),
        );
        const data = perSchema.flat();
        const error = null;

        if (error) {
          console.warn("Error fetching file classifications:", error);
          setFileClassifications({});
          setFileIsOrder({});
          setFileMetadata({});
          setFileSourceInfo({});
          return;
        }

        // Map classifications, metadata, extracted text status, and source info by file path
        const classificationMap = {};
        const isOrderMap = {};
        const metadataMap = {};
        const extractedTextSet = new Set();
        const sourceInfoMap = {};
        if (data) {
          data.forEach((item) => {
            classificationMap[item.file_path] = item.classification;
            isOrderMap[item.file_path] = !!item.is_order;
            if (
              item.metadata &&
              typeof item.metadata === "object" &&
              Object.keys(item.metadata).length > 0
            ) {
              metadataMap[item.file_path] = item.metadata;
            }
            if (item.full_text) {
              extractedTextSet.add(item.file_path);
            }
            // Store source info (fax vs report with timestamp)
            sourceInfoMap[item.file_path] = {
              isFax: item.is_faxed_report || false,
              timestamp: item.created_at,
              assignedByName: item.uploaded_by_name || null,
            };
          });
        }

        setFileClassifications(classificationMap);
        setFileIsOrder(isOrderMap);
        setFileMetadata(metadataMap);
        setFilesWithExtractedText(extractedTextSet);
        setFileSourceInfo(sourceInfoMap);
      } catch (error) {
        console.warn("Error fetching file classifications:", error);
        setFileClassifications({});
        setFileIsOrder({});
      } finally {
        // Ready either way: a failed fetch must still let the list through,
        // just without chips.
        setClassificationsReady(true);
      }
    };

    if (isReportsExplorerVisible && currentFolderFiles) {
      setClassificationsReady(false);
      fetchFileClassifications();
    }
  }, [
    currentFolderFiles,
    currentFolderPath,
    isReportsExplorerVisible,
    inlineExplorerMountNode,
  ]);

  // Re-fetch classifications when files finish processing
  useEffect(() => {
    const prevProcessingFiles = prevProcessingFilesRef.current;
    const prevKeys = Object.keys(prevProcessingFiles);
    const currentKeys = Object.keys(processingFiles);

    // Check if any file just finished processing (was in prev but not in current)
    const finishedFiles = prevKeys.filter((key) => !currentKeys.includes(key));

    if (
      finishedFiles.length > 0 &&
      isReportsExplorerVisible &&
      currentFolderFiles &&
      currentFolderFiles.length > 0
    ) {
      rLog(
        "🔄 ResultsTable: Files finished processing, re-fetching classifications:",
        finishedFiles,
      );

      // Delay to ensure backend has written classifications to database
      const timeoutId = setTimeout(async () => {
        const supabase = await getSupabase();

        // Build file paths from current folder files
        const filePaths = currentFolderFiles
          .filter((file) =>
            REPORT_FILE_RE.test(file.name),
          )
          .map((file) => {
            const path = currentFolderPath
              ? `${currentFolderPath}/${file.name}`
              : file.name;
            return path;
          });

        if (filePaths.length === 0) return;

        try {
          // Fetch classifications, metadata, full_text, and source info for all files
          const { data, error } = await supabase
            .from("file_classification_cache")
            .select(
              "file_path, classification, is_order, metadata, full_text, is_faxed_report, created_at",
            )
            .in("file_path", filePaths);

          if (!error && data) {
            // Map classifications, metadata, extracted text status, and source info by file path
            const classificationMap = {};
            const isOrderMap = {};
            const metadataMap = {};
            const extractedTextSet = new Set();
            const sourceInfoMap = {};

            data.forEach((item) => {
              classificationMap[item.file_path] = item.classification;
              isOrderMap[item.file_path] = !!item.is_order;
              if (
                item.metadata &&
                typeof item.metadata === "object" &&
                Object.keys(item.metadata).length > 0
              ) {
                metadataMap[item.file_path] = item.metadata;
              }
              if (item.full_text) {
                extractedTextSet.add(item.file_path);
              }
              // Store source info (fax vs report with timestamp)
              sourceInfoMap[item.file_path] = {
                isFax: item.is_faxed_report || false,
                timestamp: item.created_at,
              };
            });

            setFileClassifications(classificationMap);
            setFileIsOrder(isOrderMap);
            setFileMetadata(metadataMap);
            setFilesWithExtractedText(extractedTextSet);
            setFileSourceInfo(sourceInfoMap);
            rLog(
              `✅ ResultsTable: Re-fetched classifications for ${data.length} files`,
            );
          }
        } catch (error) {
          console.warn("Error re-fetching file classifications:", error);
        }
      }, 1000); // 1 second delay to allow backend to complete

      return () => clearTimeout(timeoutId);
    }

    // Update the ref with current processingFiles
    prevProcessingFilesRef.current = processingFiles;
  }, [
    processingFiles,
    isReportsExplorerVisible,
    currentFolderFiles,
    currentFolderPath,
  ]);

  // Handle Forms checkbox change and update database
  const handleFormsCheckboxChange = async (id) => {
    const supabase = await getSupabase();

    const newValue = !selectedForms[id];
    setSelectedForms((prev) => ({
      ...prev,
      [id]: newValue,
    }));
    try {
      const { error } = await supabase
        .from(tableName)
        .update({ forms_ind: newValue })
        .eq("id", id);
      if (error) throw error;
    } catch (error) {
      console.error("Error updating forms_ind in Supabase:", error.message);
    }
  };

  // Handle Live Transcription checkbox change and update database

  //Handle Submissions URL changes and update database
  const handleSubmissionsURLChange = async (id) => {
    //const newValue =
  };
  // Other existing functions (unchanged for brevity)
  const openShareModal = (fileId) => {
    setSelectedFileId(fileId);
    setIsShareModalOpen(true);
  };

  const closeShareModal = () => {
    setIsShareModalOpen(false);
    setEmailToShare("");
  };

  // Rename file function
  const renameFile = async () => {
    if (!selectedFileForRename || !newFileName.trim()) {
      toast({
        title: "Error",
        description: "Please enter a valid filename",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const supabase = await getSupabase();
    const oldFilePath = selectedFileForRename.path;
    const oldFileName = selectedFileForRename.name;

    // Preserve file extension
    let newFileNameWithExt = newFileName.trim();
    if (oldFileName.includes(".")) {
      const extension = oldFileName.substring(oldFileName.lastIndexOf("."));
      if (!newFileNameWithExt.endsWith(extension)) {
        newFileNameWithExt = newFileNameWithExt + extension;
      }
    }

    // Check if the filename has actually changed
    if (newFileNameWithExt === oldFileName) {
      toast({
        title: "No Change",
        description:
          "The filename is the same. Please enter a different filename to rename.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Construct new file path
    let newFilePath;
    if (currentFolderPath) {
      newFilePath = `${currentFolderPath}/${newFileNameWithExt}`;
    } else {
      newFilePath = newFileNameWithExt;
    }

    // Check if a file with the new name already exists
    try {
      const { data: existingFiles } = await supabase.storage
        .from(bucketName_files)
        .list(currentFolderPath || "", {
          search: newFileNameWithExt,
        });

      if (
        existingFiles &&
        existingFiles.some((file) => file.name === newFileNameWithExt)
      ) {
        toast({
          title: "File Already Exists",
          description: `A file named "${newFileNameWithExt}" already exists in this folder. Please choose a different name.`,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
    } catch (error) {
      // If we can't check for existing files, continue anyway (the upload will fail if it exists)
      console.warn("Could not check for existing files:", error);
    }

    setRenamingFile(true);
    try {
      // Get the classification from the old file path before renaming
      let oldClassification = null;
      let oldFullText = null;
      let oldSummary = null;
      let oldIsFaxedReport = false;

      try {
        const { data: classificationData, error: classificationError } =
          await supabase
            .from("file_classification_cache")
            .select("classification, full_text, summary, is_faxed_report")
            .eq("file_path", oldFilePath)
            .single();

        if (!classificationError && classificationData) {
          oldClassification = classificationData.classification;
          oldFullText = classificationData.full_text;
          oldSummary = classificationData.summary;
          oldIsFaxedReport = classificationData.is_faxed_report || false;
          rLog(
            `📋 Found existing classification for ${oldFilePath}: ${oldClassification}`,
          );
        }
      } catch (error) {
        console.warn(
          `Warning: Could not fetch classification for old file: ${error.message}`,
        );
      }

      // Download the file
      const { data: fileData, error: downloadError } = await supabase.storage
        .from(bucketName_files)
        .download(oldFilePath);

      if (downloadError) {
        throw new Error(`Failed to download file: ${downloadError.message}`);
      }

      // Upload with new name
      const { error: uploadError } = await supabase.storage
        .from(bucketName_files)
        .upload(newFilePath, fileData, {
          contentType: fileData.type || "application/octet-stream",
          upsert: true,
        });

      if (uploadError) {
        throw new Error(
          `Failed to upload renamed file: ${uploadError.message}`,
        );
      }

      // Update classification cache with new file path, preserving classification
      if (oldClassification) {
        try {
          // First, delete the old classification entry
          await supabase
            .from("file_classification_cache")
            .delete()
            .eq("file_path", oldFilePath);

          // Then create/update the new entry with the same classification
          const { error: cacheError } = await supabase
            .from("file_classification_cache")
            .upsert(
              {
                file_path: newFilePath,
                filename: newFileNameWithExt,
                classification: oldClassification,
                full_text: oldFullText,
                summary: oldSummary,
                is_faxed_report: oldIsFaxedReport,
              },
              {
                onConflict: "file_path",
              },
            );

          if (cacheError) {
            console.warn(
              `Warning: Failed to update classification cache: ${cacheError.message}`,
            );
          } else {
            rLog(
              `✅ Preserved classification ${oldClassification} for renamed file: ${newFilePath}`,
            );
          }
        } catch (error) {
          console.warn(
            `Warning: Error updating classification cache: ${error.message}`,
          );
        }
      }

      // Delete old file
      const { error: deleteError } = await supabase.storage
        .from(bucketName_files)
        .remove([oldFilePath]);

      if (deleteError) {
        console.warn(
          `Warning: Failed to delete old file: ${deleteError.message}`,
        );
        // Continue anyway since the new file was uploaded successfully
      }

      toast({
        title: "File Renamed",
        description: `File has been renamed to ${newFileNameWithExt}.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      onRenameFileClose();
      setSelectedFileForRename(null);
      setNewFileName("");
      setSelectedFiles(new Set());

      // Refresh the file listing
      if (refreshCurrentFolderFiles) {
        refreshCurrentFolderFiles();
      } else {
        handleViewFolderFiles(currentFolderPath);
      }
    } catch (error) {
      console.error("Error renaming file:", error);
      toast({
        title: "Rename Failed",
        description:
          error.message || "An error occurred while renaming the file.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setRenamingFile(false);
    }
  };

  // Change classification function
  const changeClassification = async () => {
    if (!selectedFileForClassification || !newClassification) {
      toast({
        title: "Error",
        description: "Please select a classification",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setChangingClassification(true);
    try {
      const supabase = await getSupabase();
      const filePath = selectedFileForClassification.path;
      const fileName = selectedFileForClassification.name;

      // Get existing classification data to preserve other fields
      const { data: existingData, error: fetchError } = await supabase
        .from("file_classification_cache")
        .select("full_text, summary, is_faxed_report")
        .eq("file_path", filePath)
        .single();

      // Update or insert classification
      const { error: updateError } = await supabase
        .from("file_classification_cache")
        .upsert(
          {
            file_path: filePath,
            filename: fileName,
            classification: newClassification,
            // Orthogonal to classification — set explicitly so unticking the box
            // actually clears the flag rather than leaving the old value behind.
            is_order: newIsOrder,
            full_text: existingData?.full_text || null,
            summary: existingData?.summary || null,
            is_faxed_report: existingData?.is_faxed_report || false,
          },
          {
            onConflict: "file_path",
          },
        );

      if (updateError) {
        throw new Error(
          `Failed to update classification: ${updateError.message}`,
        );
      }

      toast({
        title: "Classification Updated",
        description: `File classification has been changed to ${newClassification}.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      onChangeClassificationClose();
      setSelectedFileForClassification(null);
      setNewClassification("");
      setNewIsOrder(false);
      setSelectedFiles(new Set());

      // Refresh the file classifications
      if (refreshCurrentFolderFiles) {
        refreshCurrentFolderFiles();
      } else {
        handleViewFolderFiles(currentFolderPath);
      }

      // Refresh classifications
      const filePaths = currentFolderFiles
        .filter((file) =>
          REPORT_FILE_RE.test(file.name),
        )
        .map((file) => {
          const path = currentFolderPath
            ? `${currentFolderPath}/${file.name}`
            : file.name;
          return { path, name: file.name };
        });

      if (filePaths.length > 0) {
        const { data, error } = await supabase
          .from("file_classification_cache")
          .select("file_path, classification, is_order")
          .in(
            "file_path",
            filePaths.map((f) => f.path),
          );

        if (!error && data) {
          const classificationMap = {};
          const isOrderMap = {};
          data.forEach((item) => {
            classificationMap[item.file_path] = item.classification;
            isOrderMap[item.file_path] = !!item.is_order;
          });
          setFileClassifications(classificationMap);
          // Kept in step with classificationMap: refreshing one without the other
          // would leave the Order badge showing the pre-save value.
          setFileIsOrder(isOrderMap);
        }
      }
    } catch (error) {
      console.error("Error changing classification:", error);
      toast({
        title: "Update Failed",
        description:
          error.message ||
          "An error occurred while updating the classification.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setChangingClassification(false);
    }
  };

  // Reassign file function
  const reassignFile = async () => {
    if (
      !selectedFileForReassign ||
      !selectedPatientForReassign ||
      !selectedPatientForReassign.id
    ) {
      toast({
        title: "Error",
        description: "Please select a patient to reassign the file to",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setReassigningFile(true);
    try {
      const supabase = await getSupabase();
      const oldFilePath = selectedFileForReassign.path;
      const fileName = selectedFileForReassign.name;

      // Get patient data to construct new path
      // Find the EHR_ID field index
      const ehrIdIndex = prompts.findIndex((p) => p[0] === "EHR_ID");
      const ehrIdField = ehrIdIndex >= 0 ? prompts[ehrIdIndex][0] : null;

      // Build select fields - include EHR_ID if it exists
      const selectFields = [`id, ${prompts[0][0]}, date_`];
      if (ehrIdField) {
        selectFields.push(ehrIdField);
      }

      const { data: patientData, error: patientError } = await supabase
        .from(tableName)
        .select(selectFields.join(", "))
        .eq("id", selectedPatientForReassign.id)
        .single();

      if (patientError || !patientData) {
        throw new Error("Failed to fetch patient data");
      }

      const patientName = patientData[prompts[0][0]] || "Unknown";
      const scheduledDate =
        patientData.date_ || new Date().toISOString().split("T")[0];

      // Get EHR_ID if available
      const ehrId = ehrIdField ? patientData[ehrIdField] || null : null;

      // Format date as MM-DD-YY (e.g., 01-02-26)
      const dateParts = scheduledDate.split("T")[0].split("-");
      let dateFolder;
      if (dateParts.length >= 3) {
        const year = dateParts[0];
        const month = dateParts[1];
        const day = dateParts[2];
        dateFolder = `${month}-${day}-${year.slice(2)}`; // MM-DD-YY format
      } else {
        const now = new Date();
        dateFolder = `${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}-${String(now.getFullYear()).slice(2)}`;
      }

      // Get user's schema for file paths
      const userSchema = await getUserSchema();
      const schemaPrefix =
        userSchema && userSchema !== "public" ? `${userSchema}/` : "";

      // Check if folders already exist for this patient
      // For non-public schemas, look in New/Reports/{schema}/
      const basePath =
        userSchema && userSchema !== "public"
          ? `New/Reports/${userSchema}`
          : "New/Reports";
      const { data: existingFolders, error: listError } = await supabase.storage
        .from(bucketName_files)
        .list(basePath);

      let existingFolderPath = null;
      if (!listError && existingFolders) {
        const patientNameLower = patientName.toLowerCase().trim();
        const normalizedPatientName = patientNameLower
          .replace(/\s+/g, " ")
          .replace(/\s*,\s*/g, ", ");

        // Look for existing folders matching this patient
        for (const folder of existingFolders) {
          if (folder.name.includes(".")) continue; // Skip files

          const folderNameLower = folder.name.toLowerCase();
          const folderBaseName = folderNameLower.includes("_")
            ? folderNameLower.split("_")[0].trim()
            : "";
          const normalizedFolderBaseName = folderBaseName
            .replace(/\s+/g, " ")
            .replace(/\s*,\s*/g, ", ");

          // Check if folder matches patient name
          if (
            normalizedFolderBaseName === normalizedPatientName ||
            folderNameLower.startsWith(`${normalizedPatientName}_`) ||
            folderNameLower.startsWith(`${normalizedPatientName} `)
          ) {
            // Check if this folder has the date folder we need
            const patientFolderPath = `${basePath}/${folder.name}`;
            const { data: dateFolders, error: dateFoldersError } =
              await supabase.storage
                .from(bucketName_files)
                .list(patientFolderPath);

            if (!dateFoldersError && dateFolders) {
              const hasDateFolder = dateFolders.some(
                (item) => !item.name.includes(".") && item.name === dateFolder,
              );

              if (hasDateFolder) {
                // Found existing folder with the date folder - use it
                existingFolderPath = `${patientFolderPath}/${dateFolder}`;
                break;
              }
            }
          }
        }
      }

      // Use existing folder if found, otherwise create new one
      let newFolderPath;
      let newFilePath;

      if (existingFolderPath) {
        // Use existing folder
        newFolderPath = existingFolderPath;
        newFilePath = `${existingFolderPath}/${fileName}`;
      } else {
        // Construct new folder name - use EHR_ID if available, otherwise use underscore
        let folderName;
        if (
          ehrId &&
          ehrId.toString().trim() !== "" &&
          !ehrId.toString().includes("@")
        ) {
          folderName = `${patientName}_${ehrId}`;
        } else {
          folderName = `${patientName}_`;
        }

        // Construct new folder path: New/Reports/{schema}/PatientName_EHR_ID/MM-DD-YY (schema omitted for public)
        newFolderPath = `New/Reports/${schemaPrefix}${folderName}/${dateFolder}`;
        newFilePath = `${newFolderPath}/${fileName}`;
      }

      // Get the classification from the old file path before moving
      let oldClassification = null;
      let oldFullText = null;
      let oldSummary = null;
      let oldIsFaxedReport = false;

      try {
        const { data: classificationData, error: classificationError } =
          await supabase
            .from("file_classification_cache")
            .select("classification, full_text, summary, is_faxed_report")
            .eq("file_path", oldFilePath)
            .single();

        if (!classificationError && classificationData) {
          oldClassification = classificationData.classification;
          oldFullText = classificationData.full_text;
          oldSummary = classificationData.summary;
          oldIsFaxedReport = classificationData.is_faxed_report || false;
          rLog(
            `📋 Found existing classification for ${oldFilePath}: ${oldClassification}`,
          );
        }
      } catch (error) {
        console.warn(
          `Warning: Could not fetch classification for old file: ${error.message}`,
        );
      }

      // Download the file
      const { data: fileData, error: downloadError } = await supabase.storage
        .from(bucketName_files)
        .download(oldFilePath);

      if (downloadError) {
        throw new Error(`Failed to download file: ${downloadError.message}`);
      }

      // Upload to new location
      const { error: uploadError } = await supabase.storage
        .from(bucketName_files)
        .upload(newFilePath, fileData, {
          contentType: fileData.type || "application/octet-stream",
          upsert: true,
        });

      if (uploadError) {
        throw new Error(
          `Failed to upload file to new location: ${uploadError.message}`,
        );
      }

      // Update classification cache with new file path, preserving classification
      if (oldClassification) {
        try {
          // First, delete the old classification entry
          await supabase
            .from("file_classification_cache")
            .delete()
            .eq("file_path", oldFilePath);

          // Then create/update the new entry with the same classification
          const { error: cacheError } = await supabase
            .from("file_classification_cache")
            .upsert(
              {
                file_path: newFilePath,
                filename: fileName,
                classification: oldClassification,
                full_text: oldFullText,
                summary: oldSummary,
                is_faxed_report: oldIsFaxedReport,
              },
              {
                onConflict: "file_path",
              },
            );

          if (cacheError) {
            console.warn(
              `Warning: Failed to update classification cache: ${cacheError.message}`,
            );
          } else {
            rLog(
              `✅ Preserved classification ${oldClassification} for reassigned file: ${newFilePath}`,
            );
          }
        } catch (error) {
          console.warn(
            `Warning: Error updating classification cache: ${error.message}`,
          );
        }
      }

      // Delete old file
      const { error: deleteError } = await supabase.storage
        .from(bucketName_files)
        .remove([oldFilePath]);

      if (deleteError) {
        console.warn(
          `Warning: Failed to delete old file: ${deleteError.message}`,
        );
        // Continue anyway since the new file was uploaded successfully
      }

      // Don't update the reports field - it should remain pointing to existing folders
      // The file detection logic will find files in all matching folders anyway
      // Only update if the patient has no reports field set
      const { data: currentPatientData } = await supabase
        .from(tableName)
        .select("reports")
        .eq("id", selectedPatientForReassign.id)
        .single();

      if (currentPatientData && !currentPatientData.reports) {
        // Only update if reports field is empty
        const { error: updateError } = await supabase
          .from(tableName)
          .update({ reports: newFolderPath })
          .eq("id", selectedPatientForReassign.id);

        if (updateError) {
          console.warn(
            `Warning: Failed to update patient reports field: ${updateError.message}`,
          );
        }
      }

      toast({
        title: "File Reassigned",
        description: `File has been reassigned to ${patientName}.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      onReassignFileClose();
      setSelectedFileForReassign(null);
      setSelectedPatientForReassign(null);
      setSelectedFiles(new Set());

      // Refresh the file listing
      if (refreshCurrentFolderFiles) {
        refreshCurrentFolderFiles();
      } else {
        handleViewFolderFiles(currentFolderPath);
      }
    } catch (error) {
      console.error("Error reassigning file:", error);
      toast({
        title: "Reassign Failed",
        description:
          error.message || "An error occurred while reassigning the file.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setReassigningFile(false);
    }
  };

  const showAudioPlayerModal = () => {
    setisAudioPlayerModalOpen(true);
  };

  const closeAudioPlayerModal = async () => {
    setisAudioPlayerModalOpen(false);
    setAllAudios([]);
  };

  const handleAudioPlayerModal = (audio = undefined, allAudios = []) => {
    if (audio) {
      setAudio(audio);
      setAllAudios(allAudios);
      showAudioPlayerModal();
      return;
    }
  };

  const handleSort = (key) => {
    let direction;
    if (sortConfig.key === key) {
      if (sortConfig.direction === "asc") {
        direction = "desc";
      } else if (
        sortConfig.direction === "desc" &&
        sortConfig.key !== "date_" &&
        sortConfig.key !== "time_"
      ) {
        direction = null;
      } else {
        direction = "asc";
      }
    } else {
      direction = "asc";
    }
    setSortConfig({ key, direction });

    if (direction === null) {
      setResults(defaultSort);
    } else {
      const sortedResults = [...results].sort((a, b) => {
        if (key === "date_") {
          const dateA = new Date(a[key]);
          const dateB = new Date(b[key]);
          if (dateA.getTime() === dateB.getTime()) {
            const parseTime = (timeStr) => {
              if (!timeStr) return 0;
              const timeStrFormatted = timeStr.slice(0, 8);
              const [hours, minutes, seconds] = timeStrFormatted
                .split(":")
                .map(Number);
              return hours * 3600 + minutes * 60 + seconds;
            };
            const timeA = parseTime(a.time_);
            const timeB = parseTime(b.time_);
            return direction === "desc" ? timeA - timeB : timeB - timeA;
          }
          return direction === "desc" ? dateA - dateB : dateB - dateA;
        }
        if (key === "time_") {
          const timeA = timeToMinutes(
            convertScheduledTime(a.date_, a[key], a.timezone_source, viewerTimezone),
          );
          const timeB = timeToMinutes(
            convertScheduledTime(b.date_, b[key], b.timezone_source, viewerTimezone),
          );
          const cmp = timeA - timeB;
          return direction === "asc" ? cmp : -cmp;
        }
        const normalizeString = (str) => {
          if (typeof str === "string") {
            return str
              .trim()
              .replace(/\s+/g, " ")
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, "");
          }
          return String(str).trim();
        };
        const valA = normalizeString(a[key]);
        const valB = normalizeString(b[key]);
        if (valA < valB) return direction === "asc" ? -1 : 1;
        if (valA > valB) return direction === "asc" ? 1 : -1;
        return 0;
      });
      setResults(sortedResults);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleItemsPerPageChange = (event) => {
    const newItemsPerPage = parseInt(event.target.value, 10);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  useEffect(() => {
    if (schedulesDeps) {
      let differenceCount = 0;
      let d1 = -1;
      let d2 = 0;
      for (let i = 0; i < schedulesDeps.buttonTexts.length; i++) {
        if (
          schedulesDeps.buttonTexts[i] !==
            schedulesDeps.defaultButtonTexts[i] &&
          schedulesDeps.buttonTexts[i] !== "Showed up"
        ) {
          differenceCount++;
          if (d1 !== -1) {
            d2 = i;
          } else {
            d1 = i;
          }
          if (differenceCount > 0) {
            const index = schedules.findIndex(
              (obj) => obj.id === selectedRows[0],
            );
            const mod = schedulesDeps.buttonTexts;
            if (d1 === index) {
              mod[d2] === "Showed up"
                ? (mod[d2] = "Record Audio")
                : (mod[d2] = schedulesDeps.defaultButtonTexts[d2]);
              schedulesDeps.defaultButtonTexts[d2] = "Record Audio";
            } else {
              mod[d1] === "Showed up"
                ? (mod[d1] = "Record Audio")
                : (mod[d1] = schedulesDeps.defaultButtonTexts[d1]);
              schedulesDeps.defaultButtonTexts[d1] = "Record Audio";
            }
            schedulesDeps.setButtonTexts(mod);
          }
        }
      }
    }
  }, [selectedRows]);

  // Add this useEffect to fetch initial forms_ind values. One read per
  // schema: a row merged in from another provider lives in THAT schema's
  // table, so a read of the viewer's schema never returned it and this
  // reset dropped its forms_ind.
  useEffect(() => {
    let cancelled = false;
    const fetchInitialFormsStatus = async () => {
      const supabase = await getSupabase();
      const bySchema = new Map();
      for (const item of tableRows) {
        const schema = item._schema || supabaseSchema || "public";
        if (!bySchema.has(schema)) bySchema.set(schema, []);
        bySchema.get(schema).push(item.id);
      }
      const responses = await Promise.all(
        Array.from(bySchema.entries()).map(([schema, ids]) =>
          supabase
            .schema(schema)
            .from(tableName)
            .select("id, forms_ind")
            .in("id", ids),
        ),
      );

      const failed = responses.find(({ error }) => error);
      if (failed) {
        console.error("Error fetching initial forms status:", failed.error);
        return;
      }
      if (cancelled) return;

      const formsStatus = responses
        .flatMap(({ data }) => data)
        .reduce(
          (acc, row) => ({
            ...acc,
            [row.id]: row.forms_ind,
          }),
          {},
        );

      setSelectedForms(formsStatus);
    };

    if (tableRows?.length > 0) {
      fetchInitialFormsStatus();
    }
    return () => {
      cancelled = true;
    };
  }, [tableRows]);

  // Fetch unread messages count for each patient (only in AdminPage)
  // Also depends on refreshUnreadMessagesTrigger to allow manual refresh
  useEffect(() => {
    const fetchUnreadMessages = async () => {
      // Only fetch if we're in AdminPage (schedulesDeps.isAdminPage is true)
      if (!schedulesDeps?.isAdminPage) return;

      const data = schedules || results;
      if (!data || data.length === 0) return;

      try {
        const supabase = await getSupabase();
        const unreadCounts = {};

        for (const item of data) {
          // Every number of the patient (cell, home — utils/smsPhone): a
          // reply can come from either.
          const phones = [...rowPhonesNormalized(item)];
          if (!phones.length) continue;

          // Count messages from patient (incoming messages) that are not reviewed
          // Filter by:
          // 1. direction = 'inbound' (incoming from patient)
          // 2. from field contains patient's phone number
          // 3. reviewed = false (not yet read)
          // `item` can be fanned in from another selected provider schema —
          // SMS is a per-schema table, so an unqualified query here silently
          // undercounts (or zeroes) the unread badge for those rows.
          const { data: messages, error } = await dbForRow(supabase, item, supabaseSchema)
            .from("SMS")
            .select("id")
            .eq("direction", "inbound")
            .or(phones.map((ph) => `from.ilike.%${ph}%`).join(","))
            .eq("reviewed", false);

          if (!error && messages && messages.length > 0) {
            unreadCounts[item.id] = messages.length;
          }
        }

        setUnreadMessages(unreadCounts);
      } catch (error) {
        console.error("Error fetching unread messages:", error);
      }
    };

    fetchUnreadMessages();
  }, [
    schedules,
    results,
    schedulesDeps?.isAdminPage,
    schedulesDeps?.refreshUnreadMessagesTrigger,
  ]);

  const idxOfFormInd = 4;

  const classifyUploadedReportFile = async (filePath) => {
    const { BASE_URLS } = await import("../env");
    const hostname = window.location.hostname;
    const backendUrl =
      hostname === "localhost"
        ? BASE_URLS.local.backend
        : BASE_URLS.render.backend;

    let userId = null;
    try {
      const supabase = await getSupabase();
      const { data: sessionData } = await supabase.auth.getSession();
      if (sessionData?.session?.user?.id) {
        userId = sessionData.session.user.id;
      }
    } catch (e) {
      console.warn("Failed to get user session for classification:", e);
    }

    let modelIds = null;
    try {
      const savedModels = localStorage.getItem("aiAssistant_selectedModels");
      if (savedModels) modelIds = JSON.parse(savedModels);
    } catch (e) {
      console.warn("Failed to read selected models from localStorage:", e);
    }

    const response = await fetch(`${backendUrl}/api/ai-assistant/classify-file/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        file_path: filePath,
        userId,
        modelIds,
        modelId: modelIds && modelIds.length > 0 ? modelIds[0] : null,
      }),
    });

    if (!response.ok) {
      let errorMessage = "Failed to process uploaded report";
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.message || errorMessage;
      } catch (_e) {
        const text = await response.text();
        if (text) errorMessage = text;
      }
      throw new Error(errorMessage);
    }

    return response.json();
  };

  const handleInlineReportsUploadChange = async (e) => {
    const files = e.target.files;
    if (!files?.length || !currentFolderPath) return;
    setIsInlineReportsUploading(true);
    try {
      const supabase = await getSupabase();
      const bucket = bucketName_files || "files";
      for (const file of Array.from(files)) {
        const destPath = `${currentFolderPath}/${file.name}`;
        const { error } = await supabase.storage.from(bucket).upload(destPath, file, {
          upsert: false,
          contentType: file.type || undefined,
        });
        if (error) throw new Error(error.message);
        const result = await classifyUploadedReportFile(destPath);
        // classify-file returns 200 even when the cache write failed, reporting
        // it only in cache_error. Nothing used to read that, so a failed write
        // (e.g. a missing column -> PGRST204) looked like a clean upload and the
        // file silently had no classification, summary, metadata, or badges.
        if (result && result.cache_stored === false) {
          throw new Error(
            result.cache_error ||
              "The file uploaded but its classification could not be saved.",
          );
        }
      }
      toast({
        title: "Upload complete",
        description: "OCR extraction, metadata, and AI summary processing started.",
        status: "success",
        duration: 4500,
        isClosable: true,
      });
      if (refreshCurrentFolderFiles) {
        refreshCurrentFolderFiles();
      } else if (handleViewFolderFiles && currentFolderPath) {
        handleViewFolderFiles(currentFolderPath);
      }
    } catch (err) {
      toast({
        title: "Upload failed",
        description: err?.message || String(err),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsInlineReportsUploading(false);
      e.target.value = "";
    }
  };

  const renderViewReportsExplorerBody = (
    includeUpload,
    includeCloseButton,
    showFolderLevelNav = true,
    showFileActions = true,
    useInlinePreview = false,
  ) => {
    const normalizedRole = String(userRole || "")
      .trim()
      .toLowerCase();
    const showColorMap =
      normalizedRole.includes("admin") ||
      normalizedRole.includes("doctor");

    return (
        <div
          style={{
            display: "flex",
            gap: "20px",
            alignItems: "flex-start",
            flexWrap: "wrap",
            padding: "20px",
          }}
        >
          {/* Left side - Main content */}
          <div style={{ flex: "1 1 400px", minWidth: "300px" }}>
            {/* Processing indicator banner */}
            {Object.keys(processingFiles).length > 0 && (
              <Box
                bg="orange.50"
                border="1px solid"
                borderColor="orange.200"
                borderRadius="md"
                p={3}
                mb={3}
                display="flex"
                alignItems="center"
              >
                <Spinner size="sm" color="orange.500" mr={3} />
                <Box>
                  <Text fontWeight="semibold" color="orange.700" fontSize="sm">
                    Processing files...
                  </Text>
                  <Text color="orange.600" fontSize="xs">
                    {Object.values(processingFiles)
                      .map((f) => f?.message || "Extracting text and metadata")
                      .filter((v, i, a) => a.indexOf(v) === i)
                      .join(", ")}
                  </Text>
                </Box>
              </Box>
            )}

            {showFolderLevelNav && (
              <Button
                onClick={handleGoUpOneLevel}
                style={{ marginTop: "10px", marginBottom: "10px" }}
              >
                <FontAwesomeIcon icon={faArrowUp} /> Up One Level
              </Button>
            )}

            {includeUpload && (
              <>
                <input
                  ref={inlineReportsUploadInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.csv,.txt,.png,.jpg,.jpeg,.gif,.bmp,.webp,.svg,.heic,.heif,.tiff,.tif,application/pdf"
                  style={{ display: "none" }}
                  onChange={handleInlineReportsUploadChange}
                />
                <Button
                  style={{ marginTop: "10px", marginBottom: "10px" }}
                  colorScheme="green"
                  variant="solid"
                  onClick={() =>
                    inlineReportsUploadInputRef.current?.click()
                  }
                  isLoading={isInlineReportsUploading}
                  isDisabled={!currentFolderPath}
                >
                  Upload reports
                </Button>
              </>
            )}

            {/* File selection controls */}
            {showFileActions && (
            <div
              style={{
                marginBottom: "14px",
                marginTop: showFolderLevelNav ? undefined : "4px",
                display: "flex",
                gap: "10px",
                rowGap: "8px",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              <Button
                size="sm"
                colorScheme="green"
                variant="outline"
                isDisabled={selectedFiles.size === 0}
                onClick={async () => {
                  if (selectedFiles.size === 0) return;
                  // Download every selected file (Honduras: grab the whole
                  // packet in one go). Each is fetched as a blob and saved via
                  // an object URL, with a small gap so the browser doesn't
                  // collapse the rapid multi-download.
                  const names = Array.from(selectedFiles);
                  for (const name of names) {
                    try {
                      const bucket = bucketName_files || "files";
                      const f = currentFolderFiles.find(
                        (x) => x.name === name,
                      );
                      const folderPathToUse =
                        (f && f.actualFolderPath) || currentFolderPath;
                      const rawPath = folderPathToUse
                        ? `${folderPathToUse}/${name}`
                        : name;
                      const fileUrl = await getSignedUrl(bucket, rawPath);
                      if (!fileUrl) {
                        rLog("⬇️ Bulk download failed:", name, "no URL");
                        continue;
                      }
                      const resp = await fetch(fileUrl);
                      const data = await resp.blob();
                      const url = URL.createObjectURL(data);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = name;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      URL.revokeObjectURL(url);
                      await new Promise((r) => setTimeout(r, 350));
                    } catch (err) {
                      rLog("⬇️ Bulk download error:", name, err);
                    }
                  }
                }}
              >
                {selectedFiles.size > 0
                  ? `Download Selected (${selectedFiles.size})`
                  : "Download Selected"}
              </Button>
              <Button
                size="sm"
                colorScheme="red"
                onClick={async () => {
                  if (selectedFiles.size === 0) {
                    toast({
                      title: "No files selected",
                      description: "Please select at least one file to delete",
                      status: "warning",
                      duration: 3000,
                      isClosable: true,
                    });
                    return;
                  }

                  const confirmed = window.confirm(
                    `Are you sure you want to delete ${selectedFiles.size} file(s)? This action cannot be undone.`,
                  );

                  if (confirmed) {
                    try {
                      const supabase = await getSupabase();
                      const filesToDelete = Array.from(selectedFiles);
                      const filePaths = filesToDelete.map((fileName) =>
                        currentFolderPath
                          ? `${currentFolderPath}/${fileName}`
                          : fileName,
                      );

                      const { removed, errors } =
                        await deleteFilesWithCacheCleanup(
                          supabase,
                          bucketName_files,
                          filePaths,
                        );

                      if (errors.length > 0) {
                        toast({
                          title: "Error",
                          description: `Failed to delete ${errors.length} file(s)`,
                          status: "error",
                          duration: 3000,
                          isClosable: true,
                        });
                      }

                      // Refresh the file listing using the dedicated refresh function
                      if (refreshCurrentFolderFiles) {
                        refreshCurrentFolderFiles();
                      } else {
                        // Fallback to original behavior if refresh function not available
                        handleViewFolderFiles(currentFolderPath);
                      }
                      setSelectedFiles(new Set());

                      toast({
                        title: "Success",
                        description: `Deleted ${filesToDelete.length} file(s) successfully`,
                        status: "success",
                        duration: 3000,
                        isClosable: true,
                      });
                    } catch (error) {
                      console.error("Error deleting files:", error);
                      toast({
                        title: "Error",
                        description: "Failed to delete files",
                        status: "error",
                        duration: 3000,
                        isClosable: true,
                      });
                    }
                  }
                }}
                isDisabled={selectedFiles.size === 0}
              >
                Delete Selected ({selectedFiles.size})
              </Button>

              <Button
                size="sm"
                colorScheme="blue"
                variant="outline"
                onClick={() => {
                  if (
                    selectedFiles.size ===
                    currentFolderFiles.filter((file) =>
                      REPORT_FILE_RE.test(file.name),
                    ).length
                  ) {
                    setSelectedFiles(new Set());
                  } else {
                    const allFiles = currentFolderFiles
                      .filter((file) =>
                        REPORT_FILE_RE.test(file.name),
                      )
                      .map((file) => file.name);
                    setSelectedFiles(new Set(allFiles));
                  }
                }}
              >
                {selectedFiles.size ===
                currentFolderFiles.filter((file) =>
                  REPORT_FILE_RE.test(file.name),
                ).length
                  ? "Deselect All"
                  : "Select All"}
              </Button>

              <Button
                size="sm"
                colorScheme="purple"
                isDisabled={selectedFiles.size !== 1}
                opacity={selectedFiles.size === 1 ? 1 : 0.5}
                onClick={() => {
                  if (selectedFiles.size === 1) {
                    const fileName = Array.from(selectedFiles)[0];
                    const file = currentFolderFiles.find(
                      (f) => f.name === fileName,
                    );
                    if (file) {
                      const currentFileName = file.name;
                      const nameWithoutExt = currentFileName.includes(".")
                        ? currentFileName.substring(
                            0,
                            currentFileName.lastIndexOf("."),
                          )
                        : currentFileName;
                      setSelectedFileForRename({
                        name: fileName,
                        path: currentFolderPath
                          ? `${currentFolderPath}/${fileName}`
                          : fileName,
                      });
                      setNewFileName(nameWithoutExt);
                      onRenameFileOpen();
                    }
                  }
                }}
              >
                Rename
              </Button>
              <Button
                size="sm"
                colorScheme="orange"
                isDisabled={selectedFiles.size !== 1}
                opacity={selectedFiles.size === 1 ? 1 : 0.5}
                onClick={() => {
                  if (selectedFiles.size === 1) {
                    const fileName = Array.from(selectedFiles)[0];
                    const file = currentFolderFiles.find(
                      (f) => f.name === fileName,
                    );
                    if (file) {
                      setSelectedFileForReassign({
                        name: fileName,
                        path: currentFolderPath
                          ? `${currentFolderPath}/${fileName}`
                          : fileName,
                      });
                      setSelectedPatientForReassign(null);
                      onReassignFileOpen();
                    }
                  }
                }}
              >
                Reassign
              </Button>
              <Button
                size="sm"
                colorScheme="teal"
                isDisabled={selectedFiles.size !== 1}
                opacity={selectedFiles.size === 1 ? 1 : 0.5}
                onClick={() => {
                  if (selectedFiles.size === 1) {
                    const fileName = Array.from(selectedFiles)[0];
                    const file = currentFolderFiles.find(
                      (f) => f.name === fileName,
                    );
                    if (file) {
                      const filePath = currentFolderPath
                        ? `${currentFolderPath}/${fileName}`
                        : fileName;
                      const currentClassification =
                        fileClassifications[filePath] || "Other";
                      setSelectedFileForClassification({
                        name: fileName,
                        path: filePath,
                      });
                      setNewClassification(currentClassification);
                      setNewIsOrder(!!fileIsOrder[filePath]);
                      onChangeClassificationOpen();
                    }
                  }
                }}
              >
                Change Category
              </Button>
              <Button
                size="sm"
                colorScheme="blue"
                isDisabled={selectedFiles.size !== 1 || extractingText}
                opacity={selectedFiles.size === 1 ? 1 : 0.5}
                isLoading={extractingText}
                loadingText="Extracting..."
                spinnerPlacement="start"
                onClick={async () => {
                  if (selectedFiles.size === 1) {
                    const fileName = Array.from(selectedFiles)[0];
                    const file = currentFolderFiles.find(
                      (f) => f.name === fileName,
                    );
                    if (file) {
                      const filePath = currentFolderPath
                        ? `${currentFolderPath}/${fileName}`
                        : fileName;
                      await handleExtractTextMetadata(filePath, fileName);
                    }
                  }
                }}
              >
                Extract Text and Metadata
              </Button>
              {selectedFiles.size === 1 &&
                (() => {
                  const fileName = Array.from(selectedFiles)[0];
                  const filePath = currentFolderPath
                    ? `${currentFolderPath}/${fileName}`
                    : fileName;
                  const hasExtractedText = filesWithExtractedText.has(filePath);
                  if (!hasExtractedText) return null;
                  return (
                    <Button
                      size="sm"
                      colorScheme="teal"
                      variant="outline"
                      flexShrink={0}
                      whiteSpace="normal"
                      textAlign="center"
                      h="auto"
                      py={2}
                      onClick={async () => {
                        const file = currentFolderFiles.find(
                          (f) => f.name === fileName,
                        );
                        if (file) {
                          await handleViewExtractedText(filePath, fileName);
                        }
                      }}
                    >
                      View Extracted Text
                    </Button>
                  );
                })()}
            </div>
            )}

            {/* Held back until the classifications are in. Chips and rows
                both depend on is_order, and showing them a beat early is what
                flashed the orders into `All`. */}
            {!classificationsReady ? (
              <div style={{ padding: "24px 0", textAlign: "center", color: "#64748b", fontSize: 14 }}>
                Loading reports…
              </div>
            ) : (
            <>
            <ReportsFilterBar
              filters={reportVisibleFilters}
              counts={reportFilterCounts}
              activeFilter={reportActiveFilter}
              onSelect={setReportActiveFilter}
            />

            <ul style={{ listStyleType: "none", padding: 0 }}>
              {currentFolderFiles
                .filter((file) => {
                  // Folders are navigation, not reports — never filter them out, or
                  // selecting any chip strands the user with no way into a subfolder.
                  if (!REPORT_FILE_RE.test(file.name)) return true;
                  const p = currentFolderPath
                    ? `${currentFolderPath}/${file.name}`
                    : file.name;
                  return reportMatches({
                    classification: fileClassifications[p] ?? null,
                    is_order: !!fileIsOrder[p],
                  });
                })
                .map((file, index) => {
                const filePath = currentFolderPath
                  ? `${currentFolderPath}/${file.name}`
                  : file.name;
                const classification = fileClassifications[filePath];
                const metadata = fileMetadata[filePath];
                const sourceInfo = fileSourceInfo[filePath];
                const isFile = REPORT_FILE_RE.test(file.name);
                const shouldHighlight =
                  classification && isHighlightedCategory(classification);
                const fileColor = classification
                  ? getMetadataColor(classification)
                  : "#000";
                const bgColor = shouldHighlight
                  ? getMetadataBgColor(classification)
                  : "transparent";
                // Use default icon color (not grey) when no classification
                const iconColor = isFile && classification ? fileColor : "#666";

                // Check if this file is being processed (uploading, classifying, or extracting)
                const fileProcessingInfo =
                  processingFiles[filePath] || processingFiles[file.name];
                const isProcessing = !!fileProcessingInfo;

                return (
                  <li key={file.name} style={{ marginBottom: "10px" }}>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "flex-start",
                        gap: "8px",
                        width: "100%",
                        boxSizing: "border-box",
                      }}
                    >
                      {isFile && (
                        <Checkbox
                          isChecked={selectedFiles.has(file.name)}
                          onChange={(e) => {
                            e.stopPropagation();
                            const newSelectedFiles = new Set(selectedFiles);
                            if (e.target.checked) {
                              newSelectedFiles.add(file.name);
                            } else {
                              newSelectedFiles.delete(file.name);
                            }
                            setSelectedFiles(newSelectedFiles);
                          }}
                          style={{ marginTop: "2px", flexShrink: 0 }}
                        />
                      )}
                      {isProcessing ? (
                        <Spinner
                          size="xs"
                          color="orange.500"
                          style={{ marginTop: "4px", flexShrink: 0 }}
                        />
                      ) : (
                        <FontAwesomeIcon
                          icon={isFile ? faFile : faFolder}
                          style={{
                            marginTop: "4px",
                            flexShrink: 0,
                            color: iconColor,
                          }}
                        />
                      )}
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          gap: "4px",
                          flex: "1 1 180px",
                          minWidth: 0,
                          maxWidth: "100%",
                        }}
                      >
                        <span
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();

                            if (isFile) {
                              // Use prop if available, otherwise use imported value, with fallback to "files"
                              const bucket = bucketName_files || "files";
                              // Use the actual folder path where the file was found (if stored), otherwise use currentFolderPath
                              // This handles cases where files are in folders with empty EHR_ID but patient's reports field has EHR_ID
                              const folderPathToUse =
                                file.actualFolderPath || currentFolderPath;
                              // Raw (un-encoded) path — createSignedUrl encodes internally.
                              const rawPath = folderPathToUse
                                ? `${folderPathToUse}/${file.name}`
                                : file.name;
                              // Whether we'll open the file in a new tab (vs the
                              // inline preview panel).
                              const willOpenTab = !(
                                useInlinePreview &&
                                typeof onInlineFilePreview === "function"
                              );
                              // iPad/Safari: window.open() must run SYNCHRONOUSLY
                              // inside the tap gesture. Calling it after `await
                              // getSignedUrl` is killed by the iOS popup blocker
                              // (works on desktop, silently fails on iPad). So when
                              // a new tab is needed, pre-open a blank tab now and
                              // navigate it once the signed URL resolves.
                              const preOpenedTab = willOpenTab
                                ? window.open("", "_blank")
                                : null;
                              // Signed URL so the file opens/previews when `files` is private.
                              const fileUrl = await getSignedUrl(bucket, rawPath);
                              if (!fileUrl) {
                                if (preOpenedTab) preOpenedTab.close();
                                return;
                              }
                              rLog(
                                "🔍 ResultsTable - Opening file URL:",
                                fileUrl,
                              );
                              rLog(
                                "🔍 ResultsTable - Using folder path:",
                                folderPathToUse,
                              );
                              rLog(
                                "🔍 ResultsTable - File actualFolderPath:",
                                file.actualFolderPath,
                              );
                              rLog(
                                "🔍 ResultsTable - Current folder path:",
                                currentFolderPath,
                              );
                              if (!willOpenTab) {
                                onInlineFilePreview(fileUrl, file.name);
                              } else if (preOpenedTab) {
                                // Sever the opener reference (mimics noopener)
                                // before navigating the pre-opened tab.
                                try {
                                  preOpenedTab.opener = null;
                                } catch (_) {}
                                preOpenedTab.location = fileUrl;
                              } else {
                                // Synchronous open was blocked — best-effort fallback.
                                window.open(
                                  fileUrl,
                                  "_blank",
                                  "noopener,noreferrer",
                                );
                              }
                            } else {
                              const newPath = currentFolderPath
                                ? `${currentFolderPath}/${file.name}`
                                : file.name;
                              handleViewFolderFiles(newPath);
                            }
                          }}
                          style={{
                            cursor: "pointer",
                            color: fileColor,
                            textDecoration: "underline",
                            backgroundColor: bgColor,
                            padding: shouldHighlight ? "2px 6px" : "0",
                            borderRadius: shouldHighlight ? "4px" : "0",
                            fontWeight: shouldHighlight ? "600" : "normal",
                            wordBreak: "break-word",
                            overflowWrap: "anywhere",
                            maxWidth: "100%",
                          }}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              e.currentTarget.click();
                            }
                          }}
                        >
                          {file.name}
                        </span>
                        {/* Source info badge (Fax vs Report with timestamp) */}
                        {isFile && sourceInfo && (
                          <span
                            style={{
                              padding: "2px 6px",
                              borderRadius: "4px",
                              fontSize: "10px",
                              fontWeight: "500",
                              backgroundColor: sourceInfo.isFax
                                ? "#FED7D7"
                                : "#C6F6D5",
                              color: sourceInfo.isFax ? "#9B2C2C" : "#276749",
                              border: `1px solid ${sourceInfo.isFax ? "#FC8181" : "#68D391"}`,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {sourceInfo.isFax ? "Fax" : "Report"}
                            {sourceInfo.timestamp && (
                              <span style={{ marginLeft: "4px", opacity: 0.8 }}>
                                {new Date(sourceInfo.timestamp).toLocaleString(
                                  undefined,
                                  {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                    hour: "numeric",
                                    minute: "2-digit",
                                    timeZoneName: "short",
                                  },
                                )}
                              </span>
                            )}
                            {sourceInfo.assignedByName && (
                              <span
                                style={{
                                  marginLeft: "6px",
                                  opacity: 0.9,
                                  fontStyle: "italic",
                                }}
                              >
                                · Assigned by: {sourceInfo.assignedByName}
                              </span>
                            )}
                          </span>
                        )}
                        {/* Extracted metadata (e.g. document_type, patient_name) */}
                        {isFile &&
                          metadata &&
                          typeof metadata === "object" &&
                          Object.keys(metadata).length > 0 && (
                            <span
                              style={{
                                padding: "2px 6px",
                                borderRadius: "4px",
                                fontSize: "10px",
                                fontWeight: "500",
                                backgroundColor: "#EBF8FF",
                                color: "#2B6CB0",
                                border: "1px solid #90CDF4",
                                maxWidth: "280px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                display: "inline-block",
                                verticalAlign: "middle",
                              }}
                              title={Object.entries(metadata)
                                .filter(
                                  ([k, v]) =>
                                    k !== "truncation_info" &&
                                    v !== null &&
                                    v !== "",
                                )
                                .map(
                                  ([k, v]) =>
                                    `${k.replace(/_/g, " ")}: ${Array.isArray(v) ? v.join(", ") : v}`,
                                )
                                .join(" · ")}
                            >
                              {metadata.document_type ||
                                metadata.patient_name ||
                                metadata.diagnosis ||
                                "Metadata"}
                            </span>
                          )}
                        {isFile && sourceInfo?.assignedByName && (
                          <span
                            style={{
                              padding: "2px 8px",
                              borderRadius: "4px",
                              fontSize: "11px",
                              fontWeight: "500",
                              backgroundColor: "#FAF5FF",
                              color: "#553C9A",
                              border: "1px solid #D6BCFA",
                              whiteSpace: "nowrap",
                              display: "inline-block",
                              verticalAlign: "middle",
                            }}
                          >
                            {sourceInfo.isFax ? "Assigned" : "Uploaded"} by:{" "}
                            {sourceInfo.assignedByName}
                          </span>
                        )}
                        {/* Order flag — a NEW badge joining this strip. Orders are
                            orthogonal to classification, so this sits alongside the
                            colour coding rather than replacing it. */}
                        {isFile && fileIsOrder[filePath] && (
                          <span
                            style={{
                              padding: "2px 6px",
                              borderRadius: "4px",
                              fontSize: "10px",
                              fontWeight: "600",
                              backgroundColor: getOrdersBgColor(),
                              color: ORDERS_COLOR,
                              border: `1px solid ${ORDERS_COLOR}`,
                              whiteSpace: "nowrap",
                            }}
                            title={
                              fileMetadata[filePath]?.order_note ||
                              "Flagged as an order"
                            }
                          >
                            Order
                          </span>
                        )}
                        {isFile &&
                          fileIsOrder[filePath] &&
                          fileMetadata[filePath]?.order_note && (
                            <span
                              style={{
                                fontSize: "10px",
                                color: "#4A5568",
                                fontStyle: "italic",
                              }}
                            >
                              {fileMetadata[filePath].order_note}
                            </span>
                          )}
                      </div>
                      <HStack
                        spacing={2}
                        flexWrap="wrap"
                        alignItems="flex-start"
                        flexShrink={0}
                        flex="1 1 auto"
                        justifyContent="flex-start"
                        minW={0}
                        maxW="100%"
                      >
                        {isProcessing && (
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              color: "#d69e2e",
                              fontSize: "12px",
                              flexShrink: 0,
                            }}
                          >
                            <Spinner size="xs" mr={1} color="orange.500" />
                            {fileProcessingInfo?.message || "Processing..."}
                          </span>
                        )}
                        {isFile && !isProcessing && (
                          <Button
                            size="xs"
                            colorScheme="blue"
                            variant="outline"
                            flexShrink={0}
                            whiteSpace="normal"
                            textAlign="center"
                            h="auto"
                            py={1}
                            px={2}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const folderPathToUse =
                                file.actualFolderPath || currentFolderPath;
                              const filePath = folderPathToUse
                                ? `${folderPathToUse}/${file.name}`
                                : file.name;
                              handleViewExtractedText(filePath, file.name);
                            }}
                          >
                            View Extracted Text
                          </Button>
                        )}
                        {isFile && !isProcessing && (
                          <Button
                            size="xs"
                            colorScheme="green"
                            variant="outline"
                            flexShrink={0}
                            whiteSpace="normal"
                            textAlign="center"
                            h="auto"
                            py={1}
                            px={2}
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              // Download the ORIGINAL file to the user's device
                              // (Honduras order-packet workflow). Fetch the blob
                              // and trigger a real download via an object URL — a
                              // signed-URL `<a download>` is ignored cross-origin.
                              try {
                                const bucket = bucketName_files || "files";
                                const folderPathToUse =
                                  file.actualFolderPath || currentFolderPath;
                                const rawPath = folderPathToUse
                                  ? `${folderPathToUse}/${file.name}`
                                  : file.name;
                                const fileUrl = await getSignedUrl(
                                  bucket,
                                  rawPath,
                                );
                                if (!fileUrl) {
                                  rLog("⬇️ Download failed: no signed URL");
                                  return;
                                }
                                const resp = await fetch(fileUrl);
                                const data = await resp.blob();
                                const url = URL.createObjectURL(data);
                                const a = document.createElement("a");
                                a.href = url;
                                a.download = file.name;
                                document.body.appendChild(a);
                                a.click();
                                a.remove();
                                URL.revokeObjectURL(url);
                              } catch (err) {
                                rLog("⬇️ Download error:", err);
                              }
                            }}
                          >
                            Download
                          </Button>
                        )}
                        {isFile &&
                        !isProcessing &&
                        onProcessReport &&
                        (() => {
                          // Classify using metadata (from backend extraction) first, then filename
                          const reportType = classifyReport(
                            file.name,
                            metadata,
                            classification,
                          );
                          if (!reportType) return null;
                          const folderPathToUse =
                            file.actualFolderPath || currentFolderPath;
                          const filePath = folderPathToUse
                            ? `${folderPathToUse}/${file.name}`
                            : file.name;
                          return (
                            <Button
                              size="xs"
                              colorScheme="teal"
                              flexShrink={0}
                              whiteSpace="normal"
                              textAlign="center"
                              h="auto"
                              py={1}
                              px={2}
                              onClick={async (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                try {
                                  const supabase = await getSupabase();
                                  // Collect all files in this folder with the same report type.
                                  // Use currentFolderPath (not actualFolderPath) for metadata/classification
                                  // lookups because fileMetadata and fileClassifications are keyed that way.
                                  const sameTypeFiles =
                                    currentFolderFiles.filter((f) => {
                                      const fMetaPath = currentFolderPath
                                        ? `${currentFolderPath}/${f.name}`
                                        : f.name;
                                      return (
                                        classifyReport(
                                          f.name,
                                          fileMetadata[fMetaPath],
                                          fileClassifications[fMetaPath],
                                        ) === reportType
                                      );
                                    });
                                  // Fetch full_text for all same-type files in parallel.
                                  // Use actualFolderPath for cache queries (matches how files were stored).
                                  const fileTexts = await Promise.all(
                                    sameTypeFiles.map(async (f) => {
                                      const fCacheFolder =
                                        f.actualFolderPath || currentFolderPath;
                                      const fCachePath = fCacheFolder
                                        ? `${fCacheFolder}/${f.name}`
                                        : f.name;
                                      const { data: cacheData } = await supabase
                                        .from("file_classification_cache")
                                        .select("full_text")
                                        .eq("file_path", fCachePath)
                                        .maybeSingle();
                                      return {
                                        name: f.name,
                                        fullText: cacheData?.full_text || null,
                                      };
                                    }),
                                  );
                                  const filesWithText = fileTexts.filter(
                                    (ft) => ft.fullText,
                                  );
                                  if (filesWithText.length === 0) {
                                    toast({
                                      title: "No Extracted Text",
                                      description:
                                        "Extract text for these files first, then use Process Report.",
                                      status: "warning",
                                      duration: 4000,
                                      isClosable: true,
                                    });
                                    return;
                                  }
                                  // Combine texts; use separator between multiple files
                                  const combinedText =
                                    filesWithText.length === 1
                                      ? filesWithText[0].fullText
                                      : filesWithText
                                          .map(
                                            (ft) =>
                                              `--- ${ft.name} ---\n${ft.fullText}`,
                                          )
                                          .join("\n\n");
                                  // Pass a single filename only when there's exactly one file
                                  const combinedFileName =
                                    filesWithText.length === 1
                                      ? filesWithText[0].name
                                      : null;
                                  const reportFileNames = sameTypeFiles.map(
                                    (f) => f.name,
                                  );
                                  onProcessReport(
                                    filePath,
                                    combinedFileName,
                                    reportType,
                                    combinedText,
                                    reportFileNames,
                                  );
                                } catch (err) {
                                  console.error(
                                    "Error fetching report text:",
                                    err,
                                  );
                                  toast({
                                    title: "Error",
                                    description:
                                      err?.message ||
                                      "Failed to load report text",
                                    status: "error",
                                    duration: 4000,
                                    isClosable: true,
                                  });
                                }
                              }}
                              title={`Process this ${REPORT_TYPE_SHORT_LABELS[reportType] || reportType} report with AI`}
                            >
                              Process{" "}
                              {REPORT_TYPE_SHORT_LABELS[reportType] ||
                                reportType}
                            </Button>
                          );
                        })()}
                      </HStack>
                    </div>
                    {isFile &&
                      metadata &&
                      typeof metadata === "object" &&
                      Object.keys(metadata).length > 0 &&
                      (() => {
                        const visibleEntries = Object.entries(metadata)
                          .filter(
                            ([k, v]) =>
                              k !== "truncation_info" &&
                              v != null &&
                              (typeof v !== "object" ||
                                (v &&
                                  typeof v === "object" &&
                                  !v.was_truncated)),
                          )
                          .map(([k, v]) => {
                            const label = k
                              .replace(/_/g, " ")
                              .replace(/\b\w/g, (c) => c.toUpperCase());
                            const value =
                              typeof v === "object" &&
                              v !== null &&
                              !Array.isArray(v)
                                ? JSON.stringify(v)
                                : String(v);
                            return { key: k, label, value };
                          })
                          .filter((e) => e.value.trim() !== "");

                        if (visibleEntries.length === 0) return null;

                        return (
                          <div
                            style={{
                              marginLeft: "28px",
                              marginTop: "4px",
                              fontSize: "11px",
                              color: "#666",
                              padding: "6px",
                              backgroundColor: "#E6F2FF",
                              borderRadius: "4px",
                              border: "1px solid #B3D9FF",
                            }}
                          >
                            {visibleEntries.map(({ key, label, value }) => (
                              <div key={key}>
                                <strong>{label}:</strong> {value}
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                  </li>
                );
              })}
            </ul>
            </>
            )}
            {showFolderLevelNav && previousFolderPath && (
              <Button
                onClick={handleGoDownOneLevel}
                style={{ marginTop: "10px", marginBottom: "10px" }}
              >
                <FontAwesomeIcon icon={faArrowDown} /> Down One Level
              </Button>
            )}
            {includeCloseButton && (
              <button
                onClick={() => {
                  setModalIsOpen(false);
                  setSelectedFiles(new Set()); // Clear selection when modal closes
                }}
                style={{
                  marginTop: "20px",
                  padding: "6px 12px",
                  background: "#007BFF",
                  color: "#fff",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            )}
          </div>

          {showColorMap && (
            <div
              style={{
                flexShrink: 1,
                flexGrow: 0,
                flexBasis: "auto",
                overflow: "hidden",
              }}
            >
              <Box
                style={{
                  padding: "8px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "6px",
                  border: "1px solid #e0e0e0",
                  position: "sticky",
                  top: "10px",
                  minWidth: "fit-content",
                }}
              >
                <Text
                  style={{
                    fontWeight: "bold",
                    marginBottom: "6px",
                    fontSize: "12px",
                    whiteSpace: "nowrap",
                  }}
                >
                  Color Map:
                </Text>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: "4px" }}
                >
                  {Object.entries(METADATA_COLORS)
                    .slice(0, 3)
                    .map(([category, color]) => {
                      const isHighlighted = isHighlightedCategory(category);
                      const bgColor = isHighlighted
                        ? getMetadataBgColor(category)
                        : "transparent";
                      return (
                        <div
                          key={category}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            fontSize: "11px",
                          }}
                        >
                          <div
                            style={{
                              width: "12px",
                              height: "12px",
                              backgroundColor: color,
                              borderRadius: "2px",
                              border: "1px solid #ccc",
                              flexShrink: 0,
                            }}
                          />
                          <span
                            style={{
                              color: color,
                              backgroundColor: bgColor,
                              padding: isHighlighted ? "1px 3px" : "0",
                              borderRadius: isHighlighted ? "2px" : "0",
                              fontWeight: isHighlighted ? "600" : "normal",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {category}
                          </span>
                        </div>
                      );
                    })}
                  {/* Add grey = unclassified entry */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      fontSize: "11px",
                    }}
                  >
                    <div
                      style={{
                        width: "12px",
                        height: "12px",
                        backgroundColor: "#718096",
                        borderRadius: "2px",
                        border: "1px solid #ccc",
                        flexShrink: 0,
                      }}
                    />
                    <span style={{ color: "#718096", whiteSpace: "nowrap" }}>
                      Unclassified
                    </span>
                  </div>
                  {/* Orders are a flag, not a classification, so this is a hardcoded
                      sibling rather than a METADATA_COLORS entry — adding a key there
                      would shift the .slice(0, 3) above. */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      fontSize: "11px",
                    }}
                  >
                    <div
                      style={{
                        width: "12px",
                        height: "12px",
                        backgroundColor: ORDERS_COLOR,
                        borderRadius: "2px",
                        border: "1px solid #ccc",
                        flexShrink: 0,
                      }}
                    />
                    <span style={{ color: ORDERS_COLOR, whiteSpace: "nowrap" }}>
                      Order (badge)
                    </span>
                  </div>
                </div>
              </Box>
            </div>
          )}
        </div>
    );
  };

  return (
    <>
      {!globalModalOnly && (schedules ? (
        <Table colorScheme="gray" size="sm">
          <Thead bg={royal_blue}>
            <Tr>
              {showCheckboxes && (
                <Th color="white" textAlign="center">
                  Select
                </Th>
              )}
              {activeDisplayOrder
                .slice(0, 5)
                .filter((index) => !hiddenColumns.includes(prompts[index][0]))
                .map((index) => (
                  <Th
                    color="white"
                    key={prompts[index][0]}
                    width="auto"
                    py={2}
                    px={2}
                    textAlign="center"
                  >
                    {getColumnLabel(prompts[index][0], prompts[index][1])}
                  </Th>
                ))}
              {!hiddenColumns.includes("submissions_array") && (
                <Th color="white" width="auto" py={2} px={2} textAlign="center">
                  Submissions
                </Th>
              )}
              {activeDisplayOrder
                .slice(5)
                .filter((index) => !hiddenColumns.includes(prompts[index][0]))
                .map((index) => (
                  <Th
                    color="white"
                    key={prompts[index][0]}
                    width="auto"
                    py={2}
                    px={2}
                    textAlign="center"
                  >
                    {getColumnLabel(prompts[index][0], prompts[index][1])}
                  </Th>
                ))}
              <Th color="white" width="auto" py={2} px={2} textAlign="center">
                Scheduled Date
              </Th>
              <Th color="white" width="auto" py={2} px={2} textAlign="center">
                Scheduled Time
              </Th>
              {!hiddenColumns.includes("late_by") && (
                <Th
                  color="white"
                  width="auto"
                  py={2}
                  px={2}
                  whiteSpace="nowrap"
                  textAlign="center"
                >
                  Late By
                </Th>
              )}
              <Th color="white" width="auto" py={2} px={2} textAlign="center">
                Audio Tools
              </Th>
              {/* Orders — replaces the former Forms and Live Transcription columns.
                  The button only renders for patients that actually have an
                  order-flagged report, so the column stays quiet by default. */}
              <Th color="white" width="auto" py={2} px={2} textAlign="center">
                Orders
              </Th>
              {/* Msg column - only shown in AdminPage when user can access SMS (not tenant roles) */}
              {schedulesDeps?.showMsgColumn && (
                <Th color="white" width="auto" py={2} px={2} textAlign="center">
                  Msg
                </Th>
              )}
              {/* Confirm-Status — unlike Msg this is NOT gated to the Admin
                  page, so the same definition puts the column on Schedules
                  too. Patients never see it. */}
              {schedulesDeps?.showConfirmColumn && (
                <ConfirmStatusHeaderCell rows={schedules} />
              )}
              {showFiles && isNotAdmin && (
                <Th color="white" width="auto" py={2} px={2} textAlign="center">
                  JSON Files
                </Th>
              )}
              {showFiles && (
                <Th color="white" width="auto" py={2} px={2} textAlign="center">
                  Reports
                </Th>
              )}
            </Tr>
          </Thead>
          <Tbody>
            {isLoading ? (
              <Tr>
                <Td
                  colSpan={
                    showCheckboxes ? prompts.length + 9 : prompts.length + 8
                  }
                  textAlign="center"
                >
                  <Spinner size="lg" />
                </Td>
              </Tr>
            ) : fetchError ? (
              <Tr>
                <Td
                  colSpan={
                    showCheckboxes ? prompts.length + 9 : prompts.length + 8
                  }
                  textAlign="center"
                >
                  <Text color="red.500">{fetchError}</Text>
                </Td>
              </Tr>
            ) : schedules.length > 0 ? (
              schedules.map((schedule, ind) => {
              // Row gradient is picked POSITIONALLY (by row index) from a
              // hue-interleaved palette so adjacent rows are never close
              // in color and no two visible rows share a gradient until
              // the palette wraps.
              const _isCompleted = isScheduleCompleted(schedule);
              const _isDimmed = isRowDimmed({
                isCompleted: _isCompleted,
                isNoShow: schedulesDeps.noShows.includes(schedule.id),
              });
              const [_avC1] = pickRowGradientHex(ind, _isCompleted);
              const isPatientNameField = (field) =>
                field === linkColid ||
                field === "name" ||
                field === "patient_name";
              return (
                <Tr
                  key={schedule.id || `schedule-${ind}`}
                  _hover={
                    schedulesDeps.dups.includes(schedule.id) &&
                    !schedulesDeps.isInTimeRange[ind] &&
                    !selectedRows[schedule.id] &&
                    !schedulesDeps.noShows.includes(schedule.id) && {
                      bg: "gray.50",
                    }
                  }
                  onClick={(e) => {
                    // Check if we're currently on a document page and prevent navigation
                    const currentPath = window.location.pathname;
                    const currentSearch = window.location.search;

                    // Prevent navigation if on document pages
                    if (
                      currentPath.includes("/edit-document") ||
                      currentPath.includes("/view-document") ||
                      (currentPath.includes("/patient/") &&
                        currentSearch.includes("document"))
                    ) {
                      rLog(
                        "ResultsTable: Currently on document page, preventing navigation to patient page",
                      );
                      e.preventDefault();
                      e.stopPropagation();
                      return;
                    }

                    if (!schedulesDeps.noShows.includes(schedule.id)) {
                      if (schedulesDeps.isAdminPage) {
                        rLog(
                          "ResultsTable: isAdminPage is true. Calling handleCheckboxChange.",
                        );
                        handleCheckboxChange(schedule.id);
                      } else {
                        rLog(
                          "ResultsTable: isAdminPage is false. Attempting to navigate to patient details.",
                        );
                        // Directly navigate to patient page when clicking the row (not the Audio Tools button)
                        // medstudent → /clinic/:uuid (new combined page), else → /visit/:uuid (PatientPage)
                        // Cross-schema: a foreign patient (pulled from a linked
                        // schema by the providers fan-out) carries `_schema`.
                        // Append it so the chart opens against ITS schema, not
                        // the viewer's home — else the patient row isn't found
                        // there (406) and the chart renders blank.
                        const schemaQ = schedule._schema ? `&schema=${encodeURIComponent(schedule._schema)}` : '';
                        navigate(
                          `/${canAccessPage(userRole, 'clinicPage') ? 'clinic' : 'visit'}/${getPatientPageVisitPathSegment(schedule)}?date=${schedulesDeps.selectedDate}${schemaQ}`,
                          { state: { userInfo: schedule, _schema: schedule._schema } },
                        );
                      }
                    } else {
                      rLog(
                        "ResultsTable: schedule.id IS in noShows. No action taken for row click.",
                      );
                    }
                  }}
                  cursor={
                    schedulesDeps.noShows.includes(schedule.id)
                      ? "not-allowed"
                      : "pointer"
                  }
                  sx={{
                    bg: "transparent",
                    backgroundImage: "none",
                    backgroundColor: schedulesDeps.noShows.includes(schedule.id)
                      ? "#94a3b8"
                      : _isCompleted
                        ? "#f1f2f4" // = the old gray.200 row at 55% over white
                        : activePatients.has(schedule.id)
                          ? "#dcfce7"
                          : "white",
                    borderBottom: "1px solid",
                    borderColor: "gray.100",
                    // Highlight the whole row green while its document is open;
                    // selection still takes precedence with a blue outline.
                    boxShadow: selectedRows[schedule.id]
                      ? "inset 0 0 0 2px #2563eb"
                      : activePatients.has(schedule.id)
                        ? "inset 0 0 0 2px rgba(34, 197, 94, 0.55)"
                        : undefined,
                    // Dim the data cells rather than the row so the select
                    // checkbox stays at full strength (see rowDimming.js).
                    ...dimmedRowSx(_isDimmed),
                  }}
                >
                  {showCheckboxes && (
                    <Td
                      data-select-cell
                      onClick={(e) => e.stopPropagation()}
                      textAlign="center"
                    >
                      <Checkbox
                        isDisabled={schedulesDeps.noShows.includes(schedule.id)}
                        isChecked={selectedRows[schedule.id] || false}
                        onChange={() => handleCheckboxChange(schedule.id)}
                        colorScheme="blue"
                        sx={selectCheckboxSx(_isDimmed)}
                        aria-label={`Select schedule for ${schedule.name}`}
                      />
                    </Td>
                  )}
                  {activeDisplayOrder
                    .slice(0, 5)
                    .filter(
                      (index) => !hiddenColumns.includes(prompts[index][0]),
                    )
                    .map((index) => (
                      <Td
                        whiteSpace="nowrap"
                        key={prompts[index][0]}
                        textAlign="center"
                        width="auto"
                        py={2}
                        px={2}
                        color="gray.900"
                      >
                        {schedulesDeps.isAdminPage || isAdmin ? (
                          isPatientNameField(prompts[index][0]) ? (
                            <Text
                              fontWeight="semibold"
                              textDecoration="none"
                              color={_avC1}
                            >
                              {schedule[prompts[index][0]]}
                            </Text>
                          ) : prompts[index][0] === "dob" ? (
                            <Text textDecoration="none">
                              {formatDOB(schedule[prompts[index][0]]) || "N/A"}
                            </Text>
                          ) : (
                            <Text textDecoration="none">
                              {schedule[prompts[index][0]] || "N/A"}
                            </Text>
                          )
                        ) : prompts[index][0] === linkColid &&
                          schedule[colid] ? (
                          <Link
                            to={`/${canAccessPage(userRole, 'clinicPage') ? 'clinic' : 'visit'}/${getPatientPageVisitPathSegment(schedule)}`}
                            state={{ userInfo: schedule }}
                          >
                            <Text
                              fontWeight="semibold"
                              textDecoration="underline"
                              color={_avC1}
                            >
                              {schedule[prompts[index][0]]}
                            </Text>
                          </Link>
                        ) : isPatientNameField(prompts[index][0]) ? (
                          <Text fontWeight="semibold" color={_avC1}>
                            {schedule[prompts[index][0]] || "N/A"}
                          </Text>
                        ) : prompts[index][0] === "dob" ? (
                          formatDOB(schedule[prompts[index][0]]) || "N/A"
                        ) : (
                          schedule[prompts[index][0]] || "N/A"
                        )}
                        {(prompts[index][0] === "tel_num" || prompts[index][0] === "home_phone") && (
                          <PhoneStatusBadge phone={schedule[prompts[index][0]]} letter ml={1} />
                        )}
                      </Td>
                    ))}
                  {!hiddenColumns.includes("submissions_array") && (
                    <Td textAlign="center" width="auto" py={2} px={2}>
                      {linkCounts[schedule.id] > 0 ||
                      schedule.submission_url ? (
                        <HStack spacing={1} justify="center">
                          <VStack spacing={1} align="stretch">
                            <Button
                              colorScheme={
                                getButtonStyling(formTypes[schedule.id])
                                  .colorScheme
                              }
                              size="sm"
                              style={
                                getButtonStyling(formTypes[schedule.id]).style
                              }
                              onClick={async (e) => {
                                e.stopPropagation();

                                // If onViewForms callback is provided, use modal instead of inline expansion
                                if (onViewForms) {
                                  onViewForms(schedule.id, schedule);
                                  return;
                                }

                                // Fallback to inline expansion if no callback provided
                                const isCurrentlyExpanded =
                                  expandedSubmissionId === schedule.id;

                                if (isCurrentlyExpanded) {
                                  setExpandedSubmissionId(null);
                                  setSubmissionLinks([]);
                                } else {
                                  // Fetch from the array first
                                  let linksToDisplay =
                                    await extractAllSubmissionLinks(
                                      schedule.id,
                                      schedule._schema,
                                    );

                                  // If the array is empty, use the fallback URL
                                  if (
                                    linksToDisplay.length === 0 &&
                                    schedule.submission_url
                                  ) {
                                    linksToDisplay = [schedule.submission_url];
                                  }

                                  rLog(
                                    "Extracted Submission Links:",
                                    linksToDisplay,
                                  );

                                  // Categorize form types and store them
                                  const types = linksToDisplay.map((link) =>
                                    categorizeFormType(link),
                                  );
                                  setFormTypes((prev) => ({
                                    ...prev,
                                    [schedule.id]: types,
                                  }));

                                  setSubmissionLinks(linksToDisplay);
                                  setExpandedSubmissionId(schedule.id);
                                }
                              }}
                            >
                              {onViewForms
                                ? "View Forms"
                                : expandedSubmissionId === schedule.id
                                  ? "Hide Forms"
                                  : `View Forms`}
                            </Button>
                            {/* maps and displays all fetched links */}
                            {expandedSubmissionId === schedule.id &&
                              [...submissionLinks].map((link, linkIndex) => {
                                const formType = categorizeFormType(link);

                                // Extract form_id and submission_id from WTF Forms URL
                                // Format: https://wtf-forms-.../{form_id}/submission/{submission_id}
                                const extractFormIds = (url) => {
                                  try {
                                    const match = url.match(
                                      /\/([^\/]+)\/submission\/([^\/]+)/,
                                    );
                                    if (match) {
                                      return {
                                        form_id: match[1],
                                        submission_id: match[2],
                                      };
                                    }
                                  } catch (e) {
                                    console.error(
                                      "Error extracting form IDs:",
                                      e,
                                    );
                                  }
                                  return { form_id: null, submission_id: null };
                                };

                                const { form_id, submission_id } =
                                  extractFormIds(link);
                                const isFilledForm = form_id && submission_id;

                                // Function to test OCR on this form submission
                                const handleTestOCR = async (e) => {
                                  e.stopPropagation();
                                  if (!form_id || !submission_id) {
                                    toast({
                                      title: "Error",
                                      description:
                                        "Could not extract form_id and submission_id from URL",
                                      status: "error",
                                      duration: 3000,
                                      isClosable: true,
                                    });
                                    return;
                                  }

                                  try {
                                    // Step 1: Clear cached data from database to allow reprocessing
                                    toast({
                                      title: "Clearing cache",
                                      description: `Removing cached data for form ${form_id.substring(0, 8)}...`,
                                      status: "info",
                                      duration: 2000,
                                      isClosable: true,
                                    });

                                    const supabase = await getSupabase();
                                    const { error: clearError } = await supabase
                                      .from("StoringFormSubmission")
                                      .update({
                                        full_text: null,
                                        summary: null,
                                      })
                                      .eq("form_id", form_id)
                                      .eq("submission_id", submission_id);

                                    if (clearError) {
                                      console.error(
                                        "Error clearing cache:",
                                        clearError,
                                      );
                                      toast({
                                        title: "Warning",
                                        description: `Could not clear cache: ${clearError.message}. Proceeding anyway...`,
                                        status: "warning",
                                        duration: 3000,
                                        isClosable: true,
                                      });
                                    } else {
                                      toast({
                                        title: "Cache cleared",
                                        description:
                                          "Triggering OCR processing...",
                                        status: "success",
                                        duration: 2000,
                                        isClosable: true,
                                      });
                                    }

                                    // Step 2: Trigger OCR processing via webhook
                                    const { BASE_URLS } =
                                      await import("../env");
                                    const baseUrl =
                                      window.location.hostname ===
                                        "localhost" ||
                                      window.location.hostname === "127.0.0.1"
                                        ? BASE_URLS.local.backend
                                        : BASE_URLS.render.backend;

                                    const response = await fetch(
                                      `${baseUrl}/api/webhook/form-submission/`,
                                      {
                                        method: "POST",
                                        headers: {
                                          "Content-Type": "application/json",
                                        },
                                        body: JSON.stringify({
                                          form_id,
                                          submission_id,
                                        }),
                                      },
                                    );

                                    const result = await response.json();

                                    if (
                                      response.ok &&
                                      result.status === "success"
                                    ) {
                                      toast({
                                        title: "OCR Test Started",
                                        description:
                                          "Check backend logs for OCR processing details",
                                        status: "success",
                                        duration: 5000,
                                        isClosable: true,
                                      });
                                    } else {
                                      toast({
                                        title: "OCR Test Failed",
                                        description:
                                          result.error || "Unknown error",
                                        status: "error",
                                        duration: 5000,
                                        isClosable: true,
                                      });
                                    }
                                  } catch (error) {
                                    console.error("Error testing OCR:", error);
                                    toast({
                                      title: "Error",
                                      description:
                                        error.message ||
                                        "Failed to trigger OCR test",
                                      status: "error",
                                      duration: 5000,
                                      isClosable: true,
                                    });
                                  }
                                };

                                return (
                                  <HStack key={linkIndex} spacing={2}>
                                    <Button
                                      size="xs"
                                      colorScheme={
                                        formType === "filled"
                                          ? "blue"
                                          : "lightblue"
                                      }
                                      bg={
                                        formType === "filled"
                                          ? filled_button_form
                                          : empty_button_form
                                      }
                                      color="white"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        window.open(
                                          link,
                                          "_blank",
                                          "noopener,noreferrer",
                                        );
                                      }}
                                    >
                                      View Submission {linkIndex + 1}
                                    </Button>
                                    {/* {isFilledForm && (
                                    <Button
                                      size="xs"
                                      colorScheme="orange"
                                      variant="outline"
                                      onClick={handleTestOCR}
                                      title="Test OCR processing for this form submission"
                                    >
                                      Test OCR
                                    </Button>
                                  )} */}
                                  </HStack>
                                );
                              })}
                          </VStack>
                        </HStack>
                      ) : (
                        "N/A"
                      )}
                    </Td>
                  )}
                  {activeDisplayOrder
                    .slice(5)
                    .filter(
                      (index) => !hiddenColumns.includes(prompts[index][0]),
                    )
                    .map((index) => (
                      <Td
                        whiteSpace="nowrap"
                        key={prompts[index][0]}
                        textAlign="center"
                        width="auto"
                        py={2}
                        px={2}
                        color="gray.900"
                      >
                        {schedulesDeps.isAdminPage || isAdmin ? (
                          isPatientNameField(prompts[index][0]) ? (
                            <HStack spacing={2} align="center">
                              <Text
                                fontWeight="semibold"
                                textDecoration="none"
                                color={_avC1}
                              >
                                {schedule[prompts[index][0]]}
                              </Text>
                              {activePatients.has(schedule.id) && (
                                <Box
                                  as="span"
                                  display="inline-block"
                                  width="8px"
                                  height="8px"
                                  borderRadius="50%"
                                  bg="orange.400"
                                  animation="pulse 2s infinite"
                                  title="Document is being edited"
                                />
                              )}
                            </HStack>
                          ) : prompts[index][0] === "dob" ? (
                            <Text textDecoration="none">
                              {formatDOB(schedule[prompts[index][0]]) || "N/A"}
                            </Text>
                          ) : (
                            <Text textDecoration="none">
                              {schedule[prompts[index][0]] || "N/A"}
                            </Text>
                          )
                        ) : prompts[index][0] === linkColid &&
                          schedule[colid] ? (
                          <Link
                            to={`/${canAccessPage(userRole, 'clinicPage') ? 'clinic' : 'visit'}/${getPatientPageVisitPathSegment(schedule)}`}
                            state={{ userInfo: schedule }}
                          >
                            <Text
                              fontWeight="semibold"
                              textDecoration="underline"
                              color={_avC1}
                            >
                              {schedule[prompts[index][0]]}
                            </Text>
                          </Link>
                        ) : isPatientNameField(prompts[index][0]) ? (
                          <Text fontWeight="semibold" color={_avC1}>
                            {schedule[prompts[index][0]] || "N/A"}
                          </Text>
                        ) : prompts[index][0] === "dob" ? (
                          formatDOB(schedule[prompts[index][0]]) || "N/A"
                        ) : (
                          schedule[prompts[index][0]] || "N/A"
                        )}
                        {(prompts[index][0] === "tel_num" || prompts[index][0] === "home_phone") && (
                          <PhoneStatusBadge phone={schedule[prompts[index][0]]} letter ml={1} />
                        )}
                      </Td>
                    ))}
                  <Td
                    whiteSpace="nowrap"
                    textAlign="center"
                    width="auto"
                    py={2}
                    px={2}
                    color="gray.900"
                  >
                    {schedule.date_ ? convertDate(schedule.date_) : "N/A"}
                  </Td>
                  <Td
                    whiteSpace="nowrap"
                    textAlign="center"
                    width="auto"
                    py={2}
                    px={2}
                    color="gray.900"
                  >
                    <Text as="span" fontWeight="semibold" sx={{ color: _avC1 }}>
                      {schedule.time_
                        ? convertScheduledTime(
                            schedule.date_,
                            schedule.time_,
                            schedule.timezone_source,
                            viewerTimezone,
                          )
                        : "N/A"}
                    </Text>
                  </Td>
                  {!hiddenColumns.includes("late_by") && (
                    <Td
                      whiteSpace="nowrap"
                      textAlign="center"
                      width="auto"
                      py={2}
                      px={2}
                    >
                      {schedulesDeps.dups.includes(schedule.id) ? (
                        <LateBy
                          schedule={schedule}
                          lst={schedulesDeps.noShows}
                        />
                      ) : (
                        ""
                      )}
                    </Td>
                  )}
                  <Td textAlign="center" width="auto" py={2} px={2}>
                    <HStack spacing={2} justify="center">
                      <Button
                        size="sm"
                        colorScheme="blue"
                        onClick={(e) => {
                          e.stopPropagation();
                          rLog(
                            "ResultsTable: Audio Tools button clicked for index:",
                            ind + (currentPage - 1) * itemsPerPage,
                          );
                          schedulesDeps.handleRowClick(
                            ind + (currentPage - 1) * itemsPerPage,
                          );
                        }}
                      >
                        {
                          schedulesDeps.buttonTexts[
                            ind + (currentPage - 1) * itemsPerPage
                          ]
                        }
                      </Button>
                      {schedulesDeps.canShareSchedule?.(schedule) &&
                        schedulesDeps.onShareSchedule && (
                          <Box position="relative" display="inline-block">
                            <Button
                              size="sm"
                              variant="outline"
                              colorScheme="blue"
                              onClick={(e) => {
                                e.stopPropagation();
                                schedulesDeps.onShareSchedule(schedule);
                              }}
                              title="Share with another user"
                            >
                              <FontAwesomeIcon icon={faShare} />
                            </Button>
                            {(schedule.shared_users?.length || 0) > 0 && (
                              <Box
                                position="absolute"
                                top="-6px"
                                right="-6px"
                                bg="blue.500"
                                color="white"
                                borderRadius="full"
                                w="18px"
                                h="18px"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                fontSize="10px"
                                fontWeight="bold"
                                pointerEvents="none"
                              >
                                {schedule.shared_users.length}
                              </Box>
                            )}
                          </Box>
                        )}
                      {idsWithTranscripts.has(schedule.id) && (
                        <Button
                          size="xs"
                          colorScheme="purple"
                          onClick={(e) => {
                            e.stopPropagation();
                            schedulesDeps.handleViewSavedResults(schedule);
                          }}
                          title={`View saved transcript`}
                        >
                          T
                        </Button>
                      )}
                    </HStack>
                  </Td>
                  {/* Orders — replaces the former Forms and Live Transcription
                      cells. Button only renders when this patient actually has an
                      order-flagged report; otherwise the cell stays empty. */}
                  <Td
                    onClick={(e) => e.stopPropagation()}
                    textAlign="center"
                    width="auto"
                    py={2}
                    px={2}
                  >
                    {patientsWithOrders[schedule.id] && (
                      <Button
                        size="sm"
                        variant="outline"
                        borderColor={ORDERS_COLOR}
                        color={ORDERS_COLOR}
                        _hover={{ bg: getOrdersBgColor() }}
                        aria-label={`View orders for this patient`}
                        onClick={(e) => {
                          e.stopPropagation();
                          // Both the schedules grid and the View Reports modal live
                          // in this component, so the filter is just local state —
                          // no event plumbing needed.
                          setReportActiveFilter("Orders");
                          handleViewFolderFiles(
                            patientsWithOrders[schedule.id],
                            schedule.id,
                          );
                        }}
                      >
                        Orders
                      </Button>
                    )}
                  </Td>
                  {/* Msg button cell - only shown in AdminPage */}
                  {schedulesDeps?.showMsgColumn && (
                    <Td textAlign="center" width="auto" py={2} px={2}>
                      {unreadMessages[schedule.id] ? (
                        <Button
                          size="sm"
                          colorScheme="red"
                          className="blink-unread"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onViewMessages) {
                              onViewMessages(
                                schedule.id,
                                schedule,
                                unreadMessages[schedule.id] || 0,
                              );
                            }
                          }}
                        >
                          Msg
                          <Badge
                            ml={1}
                            colorScheme="red"
                            borderRadius="full"
                            px={1}
                            fontSize="xs"
                          >
                            {unreadMessages[schedule.id]}
                          </Badge>
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          colorScheme="purple"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onViewMessages) {
                              onViewMessages(
                                schedule.id,
                                schedule,
                                unreadMessages[schedule.id] || 0,
                              );
                            }
                          }}
                        >
                          Msg
                        </Button>
                      )}
                    </Td>
                  )}
                  {schedulesDeps?.showConfirmColumn && (
                    <Td textAlign="center" width="auto" py={2} px={2}>
                      <ConfirmStatusCell
                        row={schedule}
                        canEdit={schedulesDeps?.canEditConfirmation}
                        onViewMessages={() =>
                          onViewMessages && onViewMessages(schedule.id, schedule, 0)
                        }
                        onSetStatus={schedulesDeps?.onSetConfirmationStatus}
                        autoStatus={
                          schedulesDeps?.confirmationAutoStatus?.[
                            `${schedule._schema || supabaseSchema || "public"}::${schedule.id}`
                          ]
                        }
                        windowDays={schedulesDeps?.confirmationWindowDays}
                      />
                    </Td>
                  )}
                  {showFiles && isNotAdmin && (
                    <Td textAlign="center" width="auto" py={2} px={2}>
                      {schedule[data_column] ? (
                        <Button
                          backgroundColor="#DDDDDD"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewJSONFiles(schedule[data_column]);
                          }}
                        >
                          View JSON Files
                        </Button>
                      ) : (
                        "N/A"
                      )}
                    </Td>
                  )}
                  {showFiles && (
                    <Td textAlign="center" width="auto" py={2} px={2}>
                      {(() => {
                        // Fall back to the derived reports folder when the
                        // `reports` column is blank (only the fax assign flow
                        // sets it) so uploaded reports are still shown/opened.
                        const effectiveFolder =
                          schedule[file_reference_column] ||
                          buildReportsFolder(schedule);
                        const hasPath = !!effectiveFolder;
                        const hasFiles = schedulesWithFiles.has(schedule.id);
                        const shouldShow =
                          filesCheckComplete && hasPath && hasFiles;

                        // Check if any files are being processed for this schedule
                        const isProcessingForSchedule = Object.values(
                          processingFiles,
                        ).some(
                          (info) => info && info.scheduleId === schedule.id,
                        );
                        const processingInfo = Object.values(
                          processingFiles,
                        ).find(
                          (info) => info && info.scheduleId === schedule.id,
                        );

                        if (hasPath && filesCheckComplete && !hasFiles) {
                          rLog(
                            `🔍 ResultsTable: Schedule ${schedule.id} has path but no files. Path: ${schedule[file_reference_column]}, In set: ${hasFiles}, Check complete: ${filesCheckComplete}`,
                          );
                        }

                        // Show loading button if files are being processed for this schedule
                        if (isProcessingForSchedule) {
                          return (
                            <Button
                              backgroundColor="#FFF3CD"
                              size="sm"
                              isLoading={true}
                              loadingText={
                                processingInfo?.status === "uploading"
                                  ? "Uploading..."
                                  : "Processing..."
                              }
                              spinnerPlacement="start"
                              onClick={(e) => {
                                e.stopPropagation();
                                const folderPath =
                                  processingInfo?.folderPath ||
                                  effectiveFolder;
                                if (folderPath) {
                                  handleViewFolderFiles(
                                    folderPath,
                                    schedule.id,
                                  );
                                }
                              }}
                            >
                              View Reports
                            </Button>
                          );
                        }

                        return shouldShow;
                      })() ? (
                        <Button
                          backgroundColor="#DDDDDD"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            rLog(
                              "🔍 View Reports clicked for schedule:",
                              schedule,
                            );
                            rLog(
                              "🔍 file_reference_column value:",
                              schedule[file_reference_column],
                            );
                            rLog(
                              "🔍 file_reference_column name:",
                              file_reference_column,
                            );
                            handleViewFolderFiles(
                              schedule[file_reference_column] ||
                                buildReportsFolder(schedule),
                              schedule.id, // Pass patient ID
                            );
                          }}
                        >
                          View Reports
                        </Button>
                      ) : schedule[file_reference_column] ? null : ( // Don't show button if no files exist
                        "N/A"
                      )}
                    </Td>
                  )}
                </Tr>
              );
              })
            ) : (
              <Tr>
                <Td
                  colSpan={
                    showCheckboxes ? prompts.length + 9 : prompts.length + 8
                  }
                  textAlign="center"
                >
                  <Text align="center" mt={4} fontSize="lg">
                    No schedules found for the selected date and time.
                  </Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      ) : (
        <Box
          className="results-container"
          overflowX="auto"
          borderWidth="1px"
          borderRadius="lg"
          boxShadow="md"
          p={4}
        >
          <Table colorScheme="gray" size="sm">
            <Thead bg={royal_blue}>
              <Tr>
                {showCheckboxes && <Th color="white">Select</Th>}
                <Th
                  color="white"
                  onClick={() => handleSort("date_")}
                  textAlign={"center"}
                  cursor={"pointer"}
                >
                  Date{" "}
                  {sortConfig.key === "date_" ? (
                    sortConfig.direction === "asc" ? (
                      <Text display="inline" color={turquoise}>
                        ↑
                      </Text>
                    ) : (
                      <Text display="inline" color={turquoise}>
                        ↓
                      </Text>
                    )
                  ) : (
                    <Text display="inline" color={turquoise}>
                      ↓
                    </Text>
                  )}
                </Th>
                <Th
                  color="white"
                  onClick={() => handleSort("time_")}
                  textAlign={"center"}
                  cursor={"pointer"}
                >
                  Time{" "}
                  {sortConfig.key === "time_" ? (
                    sortConfig.direction === "asc" ? (
                      <Text display="inline" color={turquoise}>
                        ↑
                      </Text>
                    ) : (
                      <Text display="inline" color={turquoise}>
                        ↓
                      </Text>
                    )
                  ) : (
                    <Text display="inline" color={turquoise}>
                      ↓
                    </Text>
                  )}
                </Th>
                {prompts
                  .slice(0, 5)
                  .filter(([promptName]) => !hiddenColumns.includes(promptName))
                  .map(([promptName, index]) => (
                    <Th
                      color="white"
                      key={`${promptName}-${index}`}
                      textAlign="center"
                      width="150px"
                      onClick={() => handleSort(promptName)}
                      px={2}
                      cursor={"pointer"}
                    >
                      {getColumnLabel(promptName, promptName)}{" "}
                      {sortConfig.key === promptName
                        ? sortConfig.direction === "asc"
                          ? "↑"
                          : sortConfig.direction === "desc"
                            ? "↓"
                            : "-"
                        : "-"}
                    </Th>
                  ))}
                {!hiddenColumns.includes("submissions_array") && (
                  <Th
                    color="white"
                    textAlign={"center"}
                    width="auto"
                    py={2}
                    px={2}
                  >
                    Submissions
                  </Th>
                )}
                {prompts
                  .slice(5)
                  .filter(([promptName]) => !hiddenColumns.includes(promptName))
                  .map(([promptName, index]) => (
                    <Th
                      color="white"
                      key={`${promptName}-${index}`}
                      textAlign="center"
                      width="auto"
                      onClick={() => handleSort(promptName)}
                      py={2}
                      px={2}
                      cursor={"pointer"}
                    >
                      {getColumnLabel(promptName, promptName)}{" "}
                      {sortConfig.key === promptName
                        ? sortConfig.direction === "asc"
                          ? "↑"
                          : sortConfig.direction === "desc"
                            ? "↓"
                            : "-"
                        : "-"}
                    </Th>
                  ))}
                <Th
                  color="white"
                  textAlign={"center"}
                  width="auto"
                  py={2}
                  px={2}
                >
                  Audio
                </Th>
                {!hiddenColumns.includes("forms_ind") && (
                  <Th
                    color="white"
                    textAlign={"center"}
                    width="auto"
                    py={2}
                    px={2}
                  >
                    Forms
                  </Th>
                )}
                {/* Msg column - only shown in AdminPage (results table) */}
                {schedulesDeps?.showMsgColumn && (
                  <Th
                    color="white"
                    textAlign={"center"}
                    width="auto"
                    py={2}
                    px={2}
                  >
                    Msg
                  </Th>
                )}
                {/* Confirm-Status — unlike Msg this is NOT gated to the Admin
                    page, so the same definition puts the column on Schedules
                    too. Patients never see it. */}
                {schedulesDeps?.showConfirmColumn && (
                  <ConfirmStatusHeaderCell rows={results} />
                )}
                {showFiles && (
                  <Th
                    color="white"
                    textAlign={"center"}
                    width="auto"
                    py={2}
                    px={2}
                  >
                    JSON Files
                  </Th>
                )}
                {showFiles && (
                  <Th
                    color="white"
                    textAlign={"center"}
                    width="auto"
                    py={2}
                    px={2}
                  >
                    Reports
                  </Th>
                )}
              </Tr>
            </Thead>
            <Tbody>
              {results.map((result, index) => (
                <Tr
                  key={`result-${index}`}
                  data-testid="search-result-row"
                  // Middle-click (and Ctrl/Cmd+click) open the row's chart in
                  // a new tab, like a link would. Controls on the row keep
                  // their own behavior — the name IS a link and already does
                  // this natively.
                  onMouseDown={(e) => {
                    // Stop the browser's middle-button autoscroll from
                    // starting under the row before auxclick fires.
                    if (navigateOnRowClick && isMiddleButton(e) && !hitsRowControl(e)) {
                      e.preventDefault();
                    }
                  }}
                  onAuxClick={(e) => {
                    if (!navigateOnRowClick || !isMiddleButton(e) || hitsRowControl(e)) return;
                    e.preventDefault();
                    openVisitInNewTab(result);
                  }}
                  onClick={(e) => {
                    if (navigateOnRowClick) {
                      // Clicks that land on a control (name link, buttons,
                      // checkboxes) keep their own behavior; anywhere else on
                      // the row opens that row's document (chart) page.
                      if (hitsRowControl(e)) {
                        return;
                      }
                      if (wantsNewTab(e)) {
                        openVisitInNewTab(result);
                        return;
                      }
                      // Carry the row's date AND schema. Without them the
                      // chart opened in the viewer's home schema on today's
                      // date, so a cross-schema search hit found no visit and
                      // silently created a new document dated today instead of
                      // opening the row that was clicked.
                      const nav = visitNavFor(result, userRole);
                      if (!nav) return;
                      navigate(nav.to, { state: nav.state });
                      return;
                    }
                    handleCheckboxChange(result.id);
                    rLog(result.id);
                  }}
                  _hover={
                    !selectedRows[result.id] && {
                      bg: "gray.50",
                    }
                  }
                  cursor={navigateOnRowClick ? "pointer" : undefined}
                  sx={{
                    bg: selectedRows[result.id] ? "blue.100" : "transparent",
                  }}
                >
                  {showCheckboxes && (
                    <Td
                      onClick={(e) => e.stopPropagation()}
                      textAlign={"center"}
                    >
                      <Checkbox
                        isChecked={selectedRows[result.id] || false}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleCheckboxChange(result.id);
                        }}
                        isDisabled={false}
                      />
                    </Td>
                  )}
                  <Td
                    textAlign={"center"}
                    whiteSpace={"nowrap"}
                    width="auto"
                    py={2}
                    px={2}
                  >
                    {result.date_ ? convertDate(result.date_) : "N/A"}
                  </Td>
                  <Td textAlign={"center"} width="auto" py={2} px={2}>
                    {result.time_
                      ? convertScheduledTime(result.date_, result.time_, result.timezone_source, viewerTimezone)
                      : "N/A"}
                  </Td>
                  {prompts
                    .slice(0, 5)
                    .filter((d) => !hiddenColumns.includes(d[0]))
                    .map((d) => (
                      <Td
                        whiteSpace="nowrap"
                        key={d[0]}
                        textAlign="center"
                        width="auto"
                        py={2}
                        px={2}
                      >
                        {d[0] === linkColid && result[colid] ? (
                          <Link
                            // Same date + schema the row-click carries: the
                            // row handler deliberately ignores clicks that land
                            // on this link, so without them clicking the
                            // patient NAME opened a new document dated today.
                            to={visitNavFor(result, userRole)?.to || '#'}
                            state={visitNavFor(result, userRole)?.state}
                          >
                            <Text
                              fontWeight="semibold"
                              textDecoration="underline"
                            >
                              {result[d[0]]}
                            </Text>
                          </Link>
                        ) : (
                          result[d[0]] || "N/A"
                        )}
                        {(d[0] === "tel_num" || d[0] === "home_phone") && (
                          <PhoneStatusBadge phone={result[d[0]]} letter ml={1} />
                        )}
                        {/* Foreign-provider badge — only on the name column, and
                            only for rows a cross-schema search pulled in from a
                            schema other than the viewer's own. Inline-styled
                            (Chakra table): matches the roster/schedules chip. */}
                        {d[0] === linkColid && result._schema && homeSchema && result._schema !== homeSchema && (
                          <span style={{ marginLeft: 6, display: "inline-block", fontSize: "10px", fontWeight: 600, padding: "1px 5px", borderRadius: "4px", backgroundColor: "#e0e7ff", color: "#4338ca", verticalAlign: "middle" }}>{result._schema}</span>
                        )}
                        {/* Patient-level row from patients_with_ehrid, not a
                            visit. A patient can appear as both — one row per
                            visit plus their own record — so the chip names WHICH
                            TABLE the row came from. It also explains the blank
                            date column and the different destination on click. */}
                        {d[0] === linkColid && result._source === "roster" && (
                          <span style={{ marginLeft: 6, display: "inline-block", fontSize: "10px", fontWeight: 600, padding: "1px 5px", borderRadius: "4px", backgroundColor: "#dcfce7", color: "#166534", verticalAlign: "middle" }}>patient record</span>
                        )}
                      </Td>
                    ))}
                  {!hiddenColumns.includes("submissions_array") && (
                    <Td textAlign="center" width="auto" py={2} px={2}>
                      {linkCounts[result.id] > 0 || result.submission_url ? (
                        <HStack spacing={1} justify="center">
                          <VStack spacing={1} align="stretch">
                            <Button
                              colorScheme={
                                getButtonStyling(formTypes[result.id])
                                  .colorScheme
                              }
                              size="sm"
                              style={
                                getButtonStyling(formTypes[result.id]).style
                              }
                              onClick={async (e) => {
                                e.stopPropagation();

                                // If onViewForms callback is provided, use modal instead of inline expansion
                                if (onViewForms) {
                                  onViewForms(result.id, result);
                                  return;
                                }

                                // Fallback to inline expansion if no callback provided
                                const isCurrentlyExpanded =
                                  expandedSubmissionId === result.id;

                                if (isCurrentlyExpanded) {
                                  setExpandedSubmissionId(null);
                                  setSubmissionLinks([]);
                                } else {
                                  // Fetch from the array first
                                  let linksToDisplay =
                                    await extractAllSubmissionLinks(
                                      result.id,
                                      result._schema,
                                    );

                                  // If the array is empty, use the fallback URL
                                  if (
                                    linksToDisplay.length === 0 &&
                                    result.submission_url
                                  ) {
                                    linksToDisplay = [result.submission_url];
                                  }

                                  // Categorize form types and store them
                                  const types = linksToDisplay.map((link) =>
                                    categorizeFormType(link),
                                  );
                                  setFormTypes((prev) => ({
                                    ...prev,
                                    [result.id]: types,
                                  }));

                                  setSubmissionLinks(linksToDisplay);
                                  setExpandedSubmissionId(result.id);
                                }
                              }}
                            >
                              {onViewForms
                                ? "View Forms"
                                : expandedSubmissionId === result.id
                                  ? "Hide Forms"
                                  : `View Forms`}
                            </Button>
                            {expandedSubmissionId === result.id &&
                              [...submissionLinks].map((link, linkIndex) => {
                                const formType = categorizeFormType(link);

                                // Extract form_id and submission_id from WTF Forms URL
                                const extractFormIds = (url) => {
                                  try {
                                    const match = url.match(
                                      /\/([^\/]+)\/submission\/([^\/]+)/,
                                    );
                                    if (match) {
                                      return {
                                        form_id: match[1],
                                        submission_id: match[2],
                                      };
                                    }
                                  } catch (e) {
                                    console.error(
                                      "Error extracting form IDs:",
                                      e,
                                    );
                                  }
                                  return { form_id: null, submission_id: null };
                                };

                                const { form_id, submission_id } =
                                  extractFormIds(link);
                                const isFilledForm = form_id && submission_id;

                                // Function to test OCR on this form submission
                                const handleTestOCR = async (e) => {
                                  e.stopPropagation();
                                  if (!form_id || !submission_id) {
                                    toast({
                                      title: "Error",
                                      description:
                                        "Could not extract form_id and submission_id from URL",
                                      status: "error",
                                      duration: 3000,
                                      isClosable: true,
                                    });
                                    return;
                                  }

                                  try {
                                    // Step 1: Clear cached data from database to allow reprocessing
                                    toast({
                                      title: "Clearing cache",
                                      description: `Removing cached data for form ${form_id.substring(0, 8)}...`,
                                      status: "info",
                                      duration: 2000,
                                      isClosable: true,
                                    });

                                    const supabase = await getSupabase();
                                    const { error: clearError } = await supabase
                                      .from("StoringFormSubmission")
                                      .update({
                                        full_text: null,
                                        summary: null,
                                      })
                                      .eq("form_id", form_id)
                                      .eq("submission_id", submission_id);

                                    if (clearError) {
                                      console.error(
                                        "Error clearing cache:",
                                        clearError,
                                      );
                                      toast({
                                        title: "Warning",
                                        description: `Could not clear cache: ${clearError.message}. Proceeding anyway...`,
                                        status: "warning",
                                        duration: 3000,
                                        isClosable: true,
                                      });
                                    } else {
                                      toast({
                                        title: "Cache cleared",
                                        description:
                                          "Triggering OCR processing...",
                                        status: "success",
                                        duration: 2000,
                                        isClosable: true,
                                      });
                                    }

                                    // Step 2: Trigger OCR processing via webhook
                                    const { BASE_URLS } =
                                      await import("../env");
                                    const baseUrl =
                                      window.location.hostname ===
                                        "localhost" ||
                                      window.location.hostname === "127.0.0.1"
                                        ? BASE_URLS.local.backend
                                        : BASE_URLS.render.backend;

                                    const response = await fetch(
                                      `${baseUrl}/api/webhook/form-submission/`,
                                      {
                                        method: "POST",
                                        headers: {
                                          "Content-Type": "application/json",
                                        },
                                        body: JSON.stringify({
                                          form_id,
                                          submission_id,
                                        }),
                                      },
                                    );

                                    const result = await response.json();

                                    if (
                                      response.ok &&
                                      result.status === "success"
                                    ) {
                                      toast({
                                        title: "OCR Test Started",
                                        description:
                                          "Check backend logs for OCR processing details",
                                        status: "success",
                                        duration: 5000,
                                        isClosable: true,
                                      });
                                    } else {
                                      toast({
                                        title: "OCR Test Failed",
                                        description:
                                          result.error || "Unknown error",
                                        status: "error",
                                        duration: 5000,
                                        isClosable: true,
                                      });
                                    }
                                  } catch (error) {
                                    console.error("Error testing OCR:", error);
                                    toast({
                                      title: "Error",
                                      description:
                                        error.message ||
                                        "Failed to trigger OCR test",
                                      status: "error",
                                      duration: 5000,
                                      isClosable: true,
                                    });
                                  }
                                };

                                return (
                                  <HStack key={linkIndex} spacing={2}>
                                    <Button
                                      size="xs"
                                      colorScheme={
                                        formType === "filled"
                                          ? "blue"
                                          : "lightblue"
                                      }
                                      bg={
                                        formType === "filled"
                                          ? filled_button_form
                                          : empty_button_form
                                      }
                                      color="white"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        window.open(
                                          link,
                                          "_blank",
                                          "noopener,noreferrer",
                                        );
                                      }}
                                    >
                                      View Submission {linkIndex + 1}
                                    </Button>
                                    {/* {isFilledForm && (
                                    <Button
                                      size="xs"
                                      colorScheme="orange"
                                      variant="outline"
                                      onClick={handleTestOCR}
                                      title="Test OCR processing for this form submission"
                                    >
                                      Test OCR
                                    </Button>
                                  )} */}
                                  </HStack>
                                );
                              })}
                          </VStack>
                        </HStack>
                      ) : (
                        "N/A"
                      )}
                    </Td>
                  )}
                  {prompts
                    .slice(5)
                    .filter((d) => !hiddenColumns.includes(d[0]))
                    .map((d) => (
                      <Td
                        whiteSpace="nowrap"
                        key={d[0]}
                        textAlign="center"
                        width="auto"
                        py={2}
                        px={2}
                      >
                        {d[0] === linkColid && result[colid] ? (
                          <Link
                            // Same date + schema the row-click carries: the
                            // row handler deliberately ignores clicks that land
                            // on this link, so without them clicking the
                            // patient NAME opened a new document dated today.
                            to={visitNavFor(result, userRole)?.to || '#'}
                            state={visitNavFor(result, userRole)?.state}
                          >
                            <Text
                              fontWeight="semibold"
                              textDecoration="underline"
                            >
                              {result[d[0]]}
                            </Text>
                          </Link>
                        ) : (
                          result[d[0]] || "N/A"
                        )}
                        {(d[0] === "tel_num" || d[0] === "home_phone") && (
                          <PhoneStatusBadge phone={result[d[0]]} letter ml={1} />
                        )}
                        {/* Foreign-provider badge — only on the name column, and
                            only for rows a cross-schema search pulled in from a
                            schema other than the viewer's own. Inline-styled
                            (Chakra table): matches the roster/schedules chip. */}
                        {d[0] === linkColid && result._schema && homeSchema && result._schema !== homeSchema && (
                          <span style={{ marginLeft: 6, display: "inline-block", fontSize: "10px", fontWeight: 600, padding: "1px 5px", borderRadius: "4px", backgroundColor: "#e0e7ff", color: "#4338ca", verticalAlign: "middle" }}>{result._schema}</span>
                        )}
                        {/* Patient-level row from patients_with_ehrid, not a
                            visit. A patient can appear as both — one row per
                            visit plus their own record — so the chip names WHICH
                            TABLE the row came from. It also explains the blank
                            date column and the different destination on click. */}
                        {d[0] === linkColid && result._source === "roster" && (
                          <span style={{ marginLeft: 6, display: "inline-block", fontSize: "10px", fontWeight: 600, padding: "1px 5px", borderRadius: "4px", backgroundColor: "#dcfce7", color: "#166534", verticalAlign: "middle" }}>patient record</span>
                        )}
                      </Td>
                    ))}
                  <Td textAlign={"center"} width="auto" py={2} px={2}>
                    {result.more_audios?.length > 0 ? (
                      <>
                        <Button
                          colorScheme="blue"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAudioPlayerModal(
                              result.more_audios[0],
                              result.more_audios,
                            );
                          }}
                        >
                          Listen to Audio
                        </Button>
                      </>
                    ) : (
                      "No Audio"
                    )}
                  </Td>
                  {!hiddenColumns.includes("forms_ind") && (
                    <Td
                      onClick={(e) => e.stopPropagation()}
                      textAlign="center"
                      width="auto"
                      py={2}
                      px={2}
                    >
                      <Checkbox
                        isChecked={selectedForms[result.id] || false}
                        onChange={() => handleFormsCheckboxChange(result.id)}
                        colorScheme="blue"
                        aria-label={`Select forms for result ${result.id}`}
                      />
                    </Td>
                  )}
                  {/* Msg button cell - only shown in AdminPage (results table) */}
                  {schedulesDeps?.showMsgColumn && (
                    <Td textAlign="center" width="auto" py={2} px={2}>
                      {unreadMessages[result.id] ? (
                        <Button
                          size="sm"
                          colorScheme="red"
                          className="blink-unread"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onViewMessages) {
                              onViewMessages(
                                result.id,
                                result,
                                unreadMessages[result.id] || 0,
                              );
                            }
                          }}
                        >
                          Msg
                          <Badge
                            ml={1}
                            colorScheme="red"
                            borderRadius="full"
                            px={1}
                            fontSize="xs"
                          >
                            {unreadMessages[result.id]}
                          </Badge>
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          colorScheme="purple"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onViewMessages) {
                              onViewMessages(
                                result.id,
                                result,
                                unreadMessages[result.id] || 0,
                              );
                            }
                          }}
                        >
                          Msg
                        </Button>
                      )}
                    </Td>
                  )}
                  {schedulesDeps?.showConfirmColumn && (
                    <Td textAlign="center" width="auto" py={2} px={2}>
                      <ConfirmStatusCell
                        row={result}
                        canEdit={schedulesDeps?.canEditConfirmation}
                        onViewMessages={() =>
                          onViewMessages && onViewMessages(result.id, result, 0)
                        }
                        onSetStatus={schedulesDeps?.onSetConfirmationStatus}
                        autoStatus={
                          schedulesDeps?.confirmationAutoStatus?.[
                            `${result._schema || supabaseSchema || "public"}::${result.id}`
                          ]
                        }
                        windowDays={schedulesDeps?.confirmationWindowDays}
                      />
                    </Td>
                  )}
                  {showFiles && (
                    <Td textAlign={"center"} width="auto" py={2} px={2}>
                      {reportsCell(result[data_column], "View JSON Files")}
                    </Td>
                  )}
                  {showFiles && (
                    <Td textAlign={"center"} width="auto" py={2} px={2}>
                      {reportsCell(result[file_reference_column], "View Reports")}
                    </Td>
                  )}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      ))}

      <AudioPlayerModal
        audio={audio}
        allAudios={allAudios}
        scheId={undefined}
        isOpen={isAudioPlayerModalOpen}
        onClose={closeAudioPlayerModal}
      />

      {/* View Reports Modal - Non-blocking, draggable, resizable */}
      <DraggableResizableModal
        isOpen={modalIsOpen}
        onClose={() => setModalIsOpen(false)}
        title={
          <>
            <FontAwesomeIcon icon={faFolder} style={{ marginRight: "8px" }} />
            Files in Folder:{" "}
            <span
              style={{ fontSize: "14px", color: "#666", fontWeight: "normal" }}
            >
              {currentFolderPath}
            </span>
          </>
        }
        defaultSize={{
          width: Math.min(1200, window.innerWidth - 100),
          height: Math.min(700, window.innerHeight - 100),
        }}
        minSize={{ width: 500, height: 400 }}
        zIndex={9999}
        contentStyle={{ padding: "0" }}
      >
        {renderViewReportsExplorerBody(true, true, true)}
      </DraggableResizableModal>
      {inlineExplorerMountNode &&
        !modalIsOpen &&
        typeof document !== "undefined" &&
        createPortal(
          renderViewReportsExplorerBody(
            showInlineReportsUpload,
            false,
            !suppressReportsFolderNav,
            !suppressInlineFileActions,
            typeof onInlineFilePreview === "function",
          ),
          inlineExplorerMountNode,
        )}


      {/* Password Modal */}
      <PasswordModal
        isOpen={isPasswordModalOpen}
        onClose={() => {
          setIsPasswordModalOpen(false);
          setPendingAction(null);
        }}
        onSuccess={() => {
          if (pendingAction) {
            pendingAction();
            setPendingAction(null);
          }
        }}
        title="Enable Live Transcription"
      />

      {/* Rename File Modal */}
      <ReactModal
        isOpen={isRenameFileOpen}
        onRequestClose={onRenameFileClose}
        contentLabel="Rename File"
        ariaHideApp={false}
        shouldCloseOnOverlayClick={true}
        style={{
          overlay: {
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            zIndex: 10000,
          },
          content: {
            position: "relative",
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            marginRight: "-50%",
            transform: "translate(-50%, -50%)",
            width: "500px",
            maxWidth: "90vw",
            maxHeight: "80vh",
            overflowY: "auto",
            zIndex: 10001,
            backgroundColor: "white",
            borderRadius: "8px",
            padding: "20px",
            border: "none",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          },
        }}
      >
        <div style={{ marginBottom: "16px" }}>
          <h2
            style={{
              fontSize: "20px",
              fontWeight: "bold",
              marginBottom: "16px",
            }}
          >
            Rename File
          </h2>
          <button
            onClick={onRenameFileClose}
            style={{
              position: "absolute",
              top: "10px",
              right: "10px",
              background: "none",
              border: "none",
              fontSize: "24px",
              cursor: "pointer",
              color: "#666",
            }}
          >
            ×
          </button>
        </div>
        <VStack spacing={4} align="stretch">
          <Text>
            Rename file:{" "}
            <Text as="span" fontWeight="bold">
              {selectedFileForRename?.name || "N/A"}
            </Text>
          </Text>
          <FormControl>
            <FormLabel>New Filename</FormLabel>
            <Input
              value={newFileName}
              onChange={(e) => setNewFileName(e.target.value)}
              placeholder="Enter new filename (without extension)"
              isDisabled={renamingFile}
              onKeyPress={(e) => {
                if (e.key === "Enter" && !renamingFile) {
                  renameFile();
                }
              }}
            />
            <Text fontSize="xs" color="gray.500" mt={1}>
              The file extension will be preserved automatically
            </Text>
          </FormControl>
        </VStack>
        <div
          style={{
            marginTop: "24px",
            display: "flex",
            justifyContent: "flex-end",
            gap: "12px",
          }}
        >
          <Button
            variant="ghost"
            onClick={onRenameFileClose}
            isDisabled={renamingFile}
          >
            Cancel
          </Button>
          <Button
            colorScheme="purple"
            onClick={renameFile}
            isLoading={renamingFile}
          >
            Rename
          </Button>
        </div>
      </ReactModal>

      {/* Reassign File Modal */}
      <ReactModal
        isOpen={isReassignFileOpen}
        onRequestClose={() => {
          onReassignFileClose();
          setSelectedFileForReassign(null);
          setSelectedPatientForReassign(null);
        }}
        contentLabel="Reassign File to Patient"
        ariaHideApp={false}
        shouldCloseOnOverlayClick={true}
        style={{
          overlay: {
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            zIndex: 10000,
          },
          content: {
            position: "relative",
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            marginRight: "-50%",
            transform: "translate(-50%, -50%)",
            width: "98vw",
            maxWidth: "98vw",
            maxHeight: "90vh",
            overflowY: "auto",
            zIndex: 10001,
            backgroundColor: "white",
            borderRadius: "8px",
            padding: "20px",
            border: "none",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          },
        }}
      >
        <div style={{ marginBottom: "16px" }}>
          <h2
            style={{
              fontSize: "20px",
              fontWeight: "bold",
              marginBottom: "16px",
            }}
          >
            Reassign File to Patient
          </h2>
          <button
            onClick={() => {
              onReassignFileClose();
              setSelectedFileForReassign(null);
              setSelectedPatientForReassign(null);
            }}
            style={{
              position: "absolute",
              top: "10px",
              right: "10px",
              background: "none",
              border: "none",
              fontSize: "24px",
              cursor: "pointer",
              color: "#666",
            }}
          >
            ×
          </button>
        </div>
        <VStack spacing={4} align="stretch">
          <Text>
            Select a patient to reassign:{" "}
            <strong>{selectedFileForReassign?.name}</strong>
          </Text>
          <Box
            borderWidth="1px"
            borderRadius="lg"
            p={4}
            maxH="60vh"
            minH="400px"
            overflowY="auto"
            overflowX="auto"
            w="100%"
          >
            <Box w="100%" minW="1200px" overflowX="auto">
              <SearchComponent
                onPatientSelect={(patient) => {
                  setSelectedPatientForReassign(patient);
                }}
                showCheckboxes={true}
                disableUploader={true}
              />
            </Box>
          </Box>
          {selectedPatientForReassign && selectedPatientForReassign.id && (
            <Text fontSize="sm" color="green.600" fontWeight="medium">
              Selected:{" "}
              {selectedPatientForReassign[prompts[0][0]] ||
                selectedPatientForReassign.name ||
                "Unknown Patient"}
              {selectedPatientForReassign.date_ &&
                ` (Scheduled: ${selectedPatientForReassign.date_})`}
            </Text>
          )}
          {reassigningFile && (
            <Text fontSize="sm" color="gray.600">
              Reassigning file...
            </Text>
          )}
        </VStack>
        <div
          style={{
            marginTop: "24px",
            display: "flex",
            justifyContent: "flex-end",
            gap: "12px",
          }}
        >
          <Button
            variant="ghost"
            onClick={() => {
              onReassignFileClose();
              setSelectedFileForReassign(null);
              setSelectedPatientForReassign(null);
            }}
            isDisabled={reassigningFile}
          >
            Cancel
          </Button>
          <Button
            colorScheme="orange"
            onClick={() => {
              if (
                selectedPatientForReassign &&
                selectedPatientForReassign.id &&
                selectedFileForReassign
              ) {
                reassignFile();
              }
            }}
            isDisabled={
              !selectedPatientForReassign ||
              !selectedPatientForReassign.id ||
              reassigningFile
            }
          >
            Reassign
          </Button>
        </div>
      </ReactModal>

      {/* View Extracted Text Modal - Non-blocking, draggable, resizable */}
      <DraggableResizableModal
        isOpen={isViewTextModalOpen}
        onClose={onViewTextModalClose}
        title={`Extracted Text: ${viewingTextData?.filename || "Unknown"}`}
        defaultSize={{ width: 800, height: 600 }}
        minSize={{ width: 400, height: 300 }}
        zIndex={10003}
      >
        <div style={{ marginBottom: "16px" }}>
          <p
            style={{
              fontSize: "14px",
              fontWeight: "bold",
              marginBottom: "8px",
              color: "#666",
            }}
          >
            File Path:
          </p>
          <p
            style={{
              fontSize: "14px",
              color: "#333",
              fontFamily: "monospace",
              padding: "8px",
              backgroundColor: "#f7fafc",
              borderRadius: "4px",
            }}
          >
            {viewingTextData?.file_path || "N/A"}
          </p>
        </div>
        <div>
          {viewingTextData?.updated_at && (
            <p
              style={{
                fontSize: "12px",
                color: "#718096",
                marginBottom: "4px",
              }}
            >
              Last extracted:{" "}
              {new Date(viewingTextData.updated_at).toLocaleString()}
            </p>
          )}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "8px",
            }}
          >
            <p
              style={{
                fontSize: "14px",
                fontWeight: "bold",
                color: "#666",
                margin: 0,
              }}
            >
              Extracted Text ({viewingTextData?.full_text?.length || 0}{" "}
              characters):
            </p>
            <Button
              size="sm"
              colorScheme="blue"
              onClick={handleReExtractText}
              isLoading={isReExtractingText}
              loadingText="Re-extracting..."
            >
              Re-extract
            </Button>
          </div>
          <div
            style={{
              padding: "16px",
              backgroundColor: "#f7fafc",
              borderRadius: "4px",
              border: "1px solid #e2e8f0",
              flex: 1,
              overflowY: "auto",
            }}
          >
            <pre
              style={{
                fontSize: "14px",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontFamily: "monospace",
                margin: 0,
              }}
            >
              {viewingTextData?.full_text || "No text available"}
            </pre>
          </div>
        </div>
      </DraggableResizableModal>

      {/* Truncation Confirmation Modal - higher z-index to appear above DraggableResizableModal */}
      <Modal
        isOpen={isTruncationModalOpen}
        onClose={() => {}}
        isCentered
        closeOnOverlayClick={false}
        closeOnEsc={false}
      >
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent maxW="600px" containerProps={{ zIndex: 10002 }}>
          <ModalHeader fontSize="lg">Document Truncated</ModalHeader>
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm">
                The document <strong>{truncationInfo?.filename}</strong> has{" "}
                <strong>{truncationInfo?.total_pages} pages</strong>, but only
                the first{" "}
                <strong>{truncationInfo?.pages_processed} pages</strong> were
                processed for AI extraction.
              </Text>
              <Text fontSize="sm">
                There are{" "}
                <strong>
                  {truncationInfo?.remaining_pages} remaining pages
                </strong>{" "}
                that have not been processed.
              </Text>
              <Text fontSize="sm" fontWeight="medium">
                Would you like to process the remaining pages and append them to
                the extracted content?
              </Text>
              <Button
                variant="outline"
                colorScheme="gray"
                size="sm"
                fontSize="xs"
                onClick={handleViewTextFromTruncationModal}
              >
                View Extracted Text (first {truncationInfo?.pages_processed}{" "}
                pages)
              </Button>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              size="sm"
              fontSize="xs"
              mr={3}
              onClick={handleDeclineContinueExtraction}
            >
              No, this is fine
            </Button>
            <Button
              colorScheme="blue"
              size="sm"
              fontSize="xs"
              onClick={handleContinueExtraction}
              isLoading={isContinuingExtraction}
              loadingText={continueOcrProgressLabel(continueProgress)}
            >
              Yes, process remaining pages
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Change Classification Modal */}
      <ReactModal
        isOpen={isChangeClassificationOpen}
        onRequestClose={onChangeClassificationClose}
        contentLabel="Change File Classification"
        ariaHideApp={false}
        shouldCloseOnOverlayClick={true}
        style={{
          overlay: {
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            zIndex: 10000,
          },
          content: {
            position: "relative",
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            marginRight: "-50%",
            transform: "translate(-50%, -50%)",
            width: "500px",
            maxWidth: "90vw",
            maxHeight: "80vh",
            overflowY: "auto",
            zIndex: 10001,
            backgroundColor: "white",
            borderRadius: "8px",
            padding: "20px",
            border: "none",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          },
        }}
      >
        <div style={{ marginBottom: "16px" }}>
          <h2
            style={{
              fontSize: "20px",
              fontWeight: "bold",
              marginBottom: "16px",
            }}
          >
            Change File Classification
          </h2>
          <button
            onClick={onChangeClassificationClose}
            style={{
              position: "absolute",
              top: "10px",
              right: "10px",
              background: "none",
              border: "none",
              fontSize: "24px",
              cursor: "pointer",
              color: "#666",
            }}
          >
            ×
          </button>
        </div>
        <VStack spacing={4} align="stretch">
          <Text>
            Change classification for:{" "}
            <Text as="span" fontWeight="bold">
              {selectedFileForClassification?.name || "N/A"}
            </Text>
          </Text>
          <FormControl>
            <FormLabel>Classification Category</FormLabel>
            <Select
              value={newClassification}
              onChange={(e) => setNewClassification(e.target.value)}
              isDisabled={changingClassification}
            >
              <option value="Labs">Labs</option>
              <option value="Images">Images</option>
              <option value="Medical Records">Medical Records</option>
              <option value="Other">Other</option>
            </Select>
          </FormControl>
          <FormControl mt={4}>
            <Checkbox
              isChecked={newIsOrder}
              onChange={(e) => setNewIsOrder(e.target.checked)}
              isDisabled={changingClassification}
            >
              Flag as an Order (needs action)
            </Checkbox>
            <FormHelperText>
              Independent of the category — a lab result can also be an order.
            </FormHelperText>
          </FormControl>
        </VStack>
        <div
          style={{
            marginTop: "24px",
            display: "flex",
            justifyContent: "flex-end",
            gap: "12px",
          }}
        >
          <Button
            variant="ghost"
            onClick={onChangeClassificationClose}
            isDisabled={changingClassification}
          >
            Cancel
          </Button>
          <Button
            colorScheme="teal"
            onClick={changeClassification}
            isLoading={changingClassification}
          >
            Update Classification
          </Button>
        </div>
      </ReactModal>
    </>
  );
};

export default ResultsTable;
